import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import DiagnosisManagement from "./DiagnosisManagement";
import { FaPlus, FaMinus, FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import { Select, DatePicker } from 'antd';
import 'antd/dist/antd.css';
import moment from 'moment';
import C from '../lib/Config';
import swal from 'sweetalert';
import getHospitalDetails from '../lib/HospitalConfig';


import { createPortal } from 'react-dom';

// Date format constants for consistent usage across the app
const dateFormat = "DD/MM/yyyy";
const dateFormat1 = "YYYY-MM-DD";

const { Option } = Select;

const OPVisitManagement = forwardRef(({ patientId, doctorId, assessmentId }, ref) => {

  // Add a state to track assessmentId from props
  const [currentAssessmentId, setCurrentAssessmentId] = useState(assessmentId);
  

  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    
    
    // Handle HH:MM:SS format
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      const [hours, minutes, seconds] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    }
    
    // Handle HH:MM AM/PM format with variations (space, no space, lowercase)
    if (/^\d{1,2}:\d{2}\s*[APap][Mm]$/.test(timeString)) {
      const parts = timeString.split(/[\s:]/);
      let hours = parseInt(parts[0], 10);
      let minutes = parseInt(parts[1], 10);
      let isPM = false;
      
      // Find the AM/PM part
      for (const part of parts) {
        if (/^[APap][Mm]$/.test(part)) {
          isPM = part.toLowerCase() === 'pm';
          break;
        }
      }
      
      // Convert to 24-hour format
      if (isPM && hours < 12) {
        hours += 12;
      } else if (!isPM && hours === 12) {
        hours = 0;
      }
      
      return hours * 60 + minutes;
    }
    
    // For simple HH:MM format
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    }
    
    // Try to extract hours and minutes if the format is unknown
    const matchTime = timeString.match(/(\d{1,2})[:\s](\d{2})/);
    if (matchTime) {
      const hours = parseInt(matchTime[1], 10);
      const minutes = parseInt(matchTime[2], 10);
      const isPM = timeString.toLowerCase().includes('pm');
      
      // Adjust for PM if needed
      let adjustedHours = hours;
      if (isPM && hours < 12) {
        adjustedHours += 12;
      } else if (!isPM && hours === 12) {
        adjustedHours = 0;
      }
      
      return adjustedHours * 60 + minutes;
    }
    
    return 0; // Fallback for unrecognized formats
  };
  // Update Patient Height and Weight
  const updatePatientHeightWeight = async (height, weight) => {
    try {
      // Get patient ID from localStorage
      const patientId = localStorage.getItem("PatientpatientId");
      
      // Validate patient ID
      if (!patientId || isNaN(parseInt(patientId))) {
        return { success: false, error: "Invalid patient ID" };
      }

      // Get doctor ID from localStorage
      const doctorId = localStorage.getItem("doctorId") || "1";

      // Get patient email and phone from localStorage or use default values
      const patientEmail = localStorage.getItem("PatientEmail") || "";
      const patientPhone = localStorage.getItem("PatientPhone") || "";

      // First, fetch the current patient details to preserve allergies and comorbidities
      const fetchResponse = await fetch(`${C.getUrl()}/userCase/getPatientSummaryDetails?patientId=${patientId}`);
      const fetchData = await fetchResponse.json();
      
      let patientAllergies = [];
      let patientComorbidities = [];
      let allergies = "";
      let otherComorbidities = "";
      let onGoingTreatment = "";
      let otherOngoingTreatment = "";
      
      // Extract existing data if available
      if (fetchData && fetchData.message) {
        const patientDetails = JSON.parse(fetchData.message).patientDetailsDto;
        if (patientDetails) {
          patientAllergies = patientDetails.patientAllergies || [];
          patientComorbidities = patientDetails.patientComorbidities || [];
          allergies = patientDetails.allergies || "";
          otherComorbidities = patientDetails.otherComorbidities || "";
          onGoingTreatment = patientDetails.onGoingTreatment || "";
          otherOngoingTreatment = patientDetails.otherOngoingTreatment || "";
        }
      }

      // Create the payload for updating patient details, preserving existing data
      const payload = {
        patientID: parseInt(patientId),
        emailId: patientEmail,
        phoneNumber1: patientPhone,
        onGoingTreatment: onGoingTreatment,
        otherOngoingTreatment: otherOngoingTreatment,
        weight: weight,
        height: height,
        dob: localStorage.getItem("PatientDOB") || "",
        patientAllergies: patientAllergies,
        allergies: allergies,
        patientComorbidities: patientComorbidities,
        otherComorbidities: otherComorbidities,
        doctor: {
          userId: parseInt(doctorId)
        },
        referralDoctorName: "",
        referralHospital: "",
        status: "",
        dischargeDate: null
      };


      // Make the API call
      const response = await fetch(`${C.getUrl()}/userCase/updatePatientdetails`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.serviceStatus === "SUCCESS" || data.statusCode === 200) {
        return { success: true, message: "Patient height and weight updated successfully" };
      } else {
        throw new Error(data.message || "Failed to update patient details");
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update patient details"
      };
    }
  };
  const [testOptions, setTestOptions] = useState([
    // 'CBC, RFT, LFT, TSH',
    // 'CT PET SCAN WHOLE BODY',
    // 'USG (A+P)',
    // 'CHEST X RAY AP',
    // 'CBC ADVANCED',
    // 'RFT',
    // 'LFT',
    // 'CBC',
    // 'LDH',
    // 'URIC ACID',
    // 'MAMOGRAPHY',
    // 'CBC, CREAT, SGOT, SGPT',
    // 'BSL- R',
    // 'ECG',
    // '2D-ECHO',
    // 'DIRECT COOMB TEST/ INDIRECT COOMB TEST',
    // 'ATYPICAL ANTIBODY SCREEN',
    // 'IgG, IgA, IgM LEVEL, FREE CHAIN ASSAY TEST',
    // 'PROTEIN ELECTROPHORESIS',
    // 'CT THORAX',
    // 'FREE T3, T4,TSH',
    // 'URINE - R',
    // 'CBC, RFT, LFT',
    // 'CA 125',
    // 'CBC, SGOT, SGPT',
    // 'PSA',
    // 'RFT, LFT',
    // 'TSH',
    // 'PET CT SCAN WHOLE BODY'
  ]);

  const ecogOptions = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' }
  ];


  const kpsOptions = [
    { value: '0', label: '0' },
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '30', label: '30' },
    { value: '40', label: '40' },
    { value: '50', label: '50' },
    { value: '60', label: '60' },
    { value: '70', label: '70' },
    { value: '80', label: '80' },
    { value: '90', label: '90' },
    { value: '100', label: '100' }
  ];
  const [testSearchInput, setTestSearchInput] = useState('');
  const [showTestOptions, setShowTestOptions] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);

  const [activeDropdown, setActiveDropdown] = useState({
    type: null,
    index: null,
    field: null
  });

  const durationInputRefs = useRef([]);

  const [hospitalDetails] = useState(getHospitalDetails);

  // State management
  const [opVisits, setOpVisits] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [saveLoader, setSaveLoader] = useState(false);
  const printRef = useRef(null);

  // Medicine state
  const [medicinesArr, setMedicinesArr] = useState([]);
  const [savedMedicineXrefIds, setSavedMedicineXrefIds] = useState([]);
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [newItemInput, setNewItemInput] = useState({
    medicine: "",
    complaint: "",
    frequency: "",
    severity: "",
    duration: "",
    diseaseStatus: "",
    diagnosis: "",
    dose: "",
    when: "",
    prescriptionDuration: "",
    unit: "",
    instruction: "",
    treatmentType: "",
    statusOfDisease: ""
  });
  const [openMedicineDropdownIndex, setOpenMedicineDropdownIndex] = useState(-1);
  const [medicationErrors, setMedicationErrors] = useState([]);
  

  // Chief Complaints state
  const [complaintsArr, setComplaintsArr] = useState([]);
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [complaintErrors, setComplaintErrors] = useState([]);

  // Frequency state
  const [frequenciesArr, setFrequenciesArr] = useState([]);
  const [loadingFrequencies, setLoadingFrequencies] = useState(false);

  // Severity state
  const [severitiesArr, setSeveritiesArr] = useState([]);
  const [loadingSeverities, setLoadingSeverities] = useState(false);

  // Duration state
  const [durationsArr, setDurationsArr] = useState([]);
  const [loadingDurations, setLoadingDurations] = useState(false);

  // Prescription state
  const [dosesArr, setDosesArr] = useState([]);
  const [loadingDoses, setLoadingDoses] = useState(false);
const [medicinesByVisit, setMedicinesByVisit] = useState({}); // Stores medicines keyed by visitId

  // Medicine fetch status tracking (CRITICAL FIX #2)
  const [medicinesFetchStatus, setMedicinesFetchStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [medicinesFetchError, setMedicinesFetchError] = useState(null);

  const [whensArr, setWhensArr] = useState([]);
  const [loadingWhens, setLoadingWhens] = useState(false);

  const [prescriptionDurationsArr, setPrescriptionDurationsArr] = useState([]);
  const [loadingPrescriptionDurations, setLoadingPrescriptionDurations] = useState(false);

  const [unitsArr, setUnitsArr] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [instructionsArr, setInstructionsArr] = useState([]);
  const [loadingInstructions, setLoadingInstructions] = useState(false);

  const [treatmentTypesArr, setTreatmentTypesArr] = useState([]);
  const [loadingTreatmentTypes, setLoadingTreatmentTypes] = useState(false);

  const [diseaseStatusArr, setDiseaseStatusArr] = useState([]);
  const [loadingDiseaseStatus, setLoadingDiseaseStatus] = useState(false);

  const [diagnosisArr, setDiagnosisArr] = useState([]);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);

  const [openComplaintDropdownIndex, setOpenComplaintDropdownIndex] = useState(-1);
  const [openFrequencyDropdownIndex, setOpenFrequencyDropdownIndex] = useState(-1);
  const [openSeverityDropdownIndex, setOpenSeverityDropdownIndex] = useState(-1);
  const [openDoseDropdownIndex, setOpenDoseDropdownIndex] = useState(-1);
  const [openWhenDropdownIndex, setOpenWhenDropdownIndex] = useState(-1);
  const [openUnitDropdownIndex, setOpenUnitDropdownIndex] = useState(-1);
  const [openInstructionDropdownIndex, setOpenInstructionDropdownIndex] = useState(-1);
  const [openTreatmentTypeDropdownIndex, setOpenTreatmentTypeDropdownIndex] = useState(-1);
  const [openDurationDropdownIndex, setOpenDurationDropdownIndex] = useState(-1);
  const [openPrescriptionDurationDropdownIndex, setOpenPrescriptionDurationDropdownIndex] = useState(-1);
  const [openDiseaseStatusDropdownIndex, setOpenDiseaseStatusDropdownIndex] = useState(-1);
  const [openDiagnosisDropdownIndex, setOpenDiagnosisDropdownIndex] = useState(-1);



  
  const getCurrentDate = () => {
    const today = new Date();
    return formatDateForInput(today);
  };
  // Helper function to format date for display (DD/MM/YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if the date is already in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Convert from YYYY-MM-DD to DD-MM-YYYY
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  // Helper function to format date for input field (YYYY-MM-DD)
  // const formatDateForInput = (date) => {
  //   if (!date) return "";

  //   // If it's a string in DD/MM/YYYY format
  //   if (typeof date === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
  //     const parts = date.split(/[-/]/);
  //     return `${parts[2]}-${parts[1]}-${parts[0]}`;
  //   }

  //   // If it's a Date object
  //   if (date instanceof Date) {
  //     const day = String(date.getDate()).padStart(2, "0");
  //     const month = String(date.getMonth() + 1).padStart(2, "0");
  //     const year = date.getFullYear();
  //     return `${year}-${month}-${day}`;
  //   }

  //   return date;
  // };
  // Helper function to format date for input field (YYYY-MM-DD)
const formatDateForInput = (date) => {
  if (!date) return "";

  // If it's a string in DD/MM/YYYY format
  if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    const parts = date.split("/");
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  // If it's a Date object
  if (date instanceof Date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  return date;
};

// Handle date picker changes (for Ant Design DatePicker)
const handleDateChange = (date, dateString, fieldName) => {
  if (!date) {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: ''
    }));
  } else {
    // Extract date components directly from moment object to avoid timezone issues
    const momentDate = date._isAMomentObject ? date : moment(date);
    const year = momentDate.year();
    const month = String(momentDate.month() + 1).padStart(2, '0');
    const day = String(momentDate.date()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: formattedDate
    }));
  }
};
 

  // Helper function to format time (HH:MM AM/PM)
  const formatTimeForInput = (date) => {
    if (!date || !(date instanceof Date)) return "";

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    return `${hours}:${minutes} ${ampm}`;
  };

  // Sanitize function to convert null/undefined values to empty strings
  const sanitizeData = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeData(item));
    } else if (obj !== null && typeof obj === 'object') {
      const sanitized = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          sanitized[key] = (value === null || value === undefined) ? "" : value;
        }
      }
      return sanitized;
    }
    return (obj === null || obj === undefined) ? "" : obj;
  };

  // Form state
  const initializeFormData = () => {
    const today = new Date();
    const formattedDate = formatDateForInput(today);
    const formattedTime = formatTimeForInput(today);
  
    return {
      visitDate: formattedDate,
      visitTime: formattedTime,
      vitals: [{
        height: "",
        weight: "",
        pulse: "",
        temperature: "",
        bp: "",
        spo2: "",
        respiratoryRate: "",
        bloodGlucose: "",
        glucoseTestType: "",
        bsa: "",
        bmi: "",
        recordTime: formattedTime,
        sequenceNumber: 1, // Add sequence number to track order
      }],
      complaints: [{
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        date: formattedDate, // Set current date
        durationValue: "",
        durationUnit: "Days"
      }],
      historyOfPresentIllness: "",
      clinicalAssessment: {
        statusOfDisease: "",
        ecog: "",
        kps: "",
        onExamination: "",
      },
      secondaryDiagnosis: [{
        diagnosis: "",
        duration: "",
        comments: "",
        date: formattedDate, // Set current date
        durationValue: "",
        durationUnit: "Days"
      }],
      prescription: [{
        medicine: "",
        dose: "",
        when: "",
        duration: "",
        quantity: "",
        unit: "",
        instructions: "",
        durationValue: "",
        durationUnit: "Days"
      }],
      treatment: [{
        treatmentType: "",
        description: "",
        date: formattedDate, // Set current date
      }],
      adviceNotes: "",
      testsRequested: "",
      reviewNotes: "",
      referTo: "",
      nextVisitDate: "",
      nextVisitDuration: "",
      nextVisitDurationValue: "",
      nextVisitDurationUnit: "Days"
    };
  };


  const [formData, setFormData] = useState(initializeFormData());

  // Form validation state
  const [errors, setErrors] = useState({});

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);


  useEffect(() => {
    if (activeDropdown.type === 'complaint' &&
      activeDropdown.index !== null &&
      activeDropdown.field === 'duration') {

      // Get the positioning of the current input field
      const inputElement = durationInputRefs.current[activeDropdown.index];
      if (inputElement) {
        // Find the dropdown element
        const dropdownElement = document.querySelector('.duration-dropdown-menu');

        if (dropdownElement) {
          const rect = inputElement.getBoundingClientRect();

          // Position dropdown above the input
          dropdownElement.style.top = `${rect.top - dropdownElement.offsetHeight - 5}px`;
          dropdownElement.style.left = `${rect.left}px`;
          dropdownElement.style.width = `${rect.width}px`;
        }
      }
    }
  }, [activeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown.type &&
        !event.target.closest('.dropdown-container') &&
        !event.target.closest('.duration-dropdown-menu')) {
        setActiveDropdown({ type: null, index: null, field: null });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);
  // 2. Add this useEffect to close dropdown when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown({ type: null, index: null, field: null });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  useEffect(() => {
    const updateDropdownPosition = () => {
      if (activeDropdown.type === 'complaint' &&
        activeDropdown.index !== null &&
        activeDropdown.field === 'duration') {

        // Get the positioning of the current input field
        const inputElement = durationInputRefs.current[activeDropdown.index];
        if (inputElement) {
          // Find the dropdown element
          const dropdownElement = document.querySelector('.duration-dropdown-menu');

          if (dropdownElement) {
            const rect = inputElement.getBoundingClientRect();

            // Position dropdown BELOW the input instead of above
            dropdownElement.style.top = `${rect.bottom + 5}px`; // Add 5px spacing
            dropdownElement.style.left = `${rect.left}px`;
            dropdownElement.style.width = `${rect.width}px`;
          }
        }
      }
    };

    // Call once initially
    updateDropdownPosition();

    // Set up scroll and resize event listeners
    window.addEventListener('scroll', updateDropdownPosition);
    window.addEventListener('resize', updateDropdownPosition);

    // Clean up
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [activeDropdown]);

  // Fetch OP visits when component mounts or patientId changes
  useEffect(() => {
    const patId = localStorage.getItem("PatientpatientId");
    if (patId) {
      fetchPatientOpVisits();
    }
  }, [patientId]);

  const showOPVisitForm = async (visitIndex = null, isDischarge = false) => {
    // Parameters:
    // visitIndex: null for new visit, or index in opVisits array to edit
    // isDischarge: true to prefill discharge date/time (for Discharge Summary option)
    // 
    // Usage examples:
    // showOPVisitForm()                 - Create new OP visit
    // showOPVisitForm(0)                - Edit existing visit at index 0
    // showOPVisitForm(null, true)       - Create new discharge summary (prefills date/time)
    
    if (visitIndex !== null) {
      // Edit mode - populate form with existing data
      const visitToEdit = opVisits[visitIndex];

      // If the visit being edited has only a single vitals object, convert it to an array
      let vitalsArray = Array.isArray(visitToEdit.vitals) ? visitToEdit.vitals : [visitToEdit.vitals];

      // Sanitize null values to empty strings to prevent React warnings
      vitalsArray = sanitizeData(vitalsArray);

      // Ensure each vitals record has a sequence number
      vitalsArray = vitalsArray.map((vital, idx) => ({
        ...vital,
        sequenceNumber: idx + 1
      }));

      // Parse and prepare complaints to handle duration properly
      const parsedComplaints = (visitToEdit.complaints ? sanitizeData(visitToEdit.complaints) : []).map(complaint => {
        // Parse duration value and unit if duration exists
        let durationValue = "";
        let durationUnit = "Days"; // Default unit

        if (complaint.duration) {
          // RegEx to extract numeric value and text unit from duration string
          const durationMatch = complaint.duration.match(/^(\d+)\s*(.*)$/);
          if (durationMatch) {
            durationValue = durationMatch[1]; // Numeric part
            durationUnit = durationMatch[2].trim() || "Days"; // Text part or default to "Days"

            // Make sure the first letter is capitalized for consistency
            durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();

            // Normalize to one of our standard units
            if (["Day", "day", "days"].includes(durationUnit)) {
              durationUnit = "Days";
            } else if (["Week", "week", "weeks"].includes(durationUnit)) {
              durationUnit = "Weeks";
            } else if (["Month", "month", "months"].includes(durationUnit)) {
              durationUnit = "Months";
            } else if (["Year", "year", "years"].includes(durationUnit)) {
              durationUnit = "Years";
            }
          }
        }

        return {
          ...complaint,
          durationValue,
          durationUnit
        };
      });

      // Parse and prepare secondary diagnosis to handle duration properly
      const parsedSecondaryDiagnosis = (visitToEdit.secondaryDiagnosis ? sanitizeData(visitToEdit.secondaryDiagnosis) : []).map(diagnosis => {
        // Parse duration value and unit if duration exists
        let durationValue = "";
        let durationUnit = "Days"; // Default unit

        if (diagnosis.duration) {
          // RegEx to extract numeric value and text unit from duration string
          const durationMatch = diagnosis.duration.match(/^(\d+)\s*(.*)$/);
          if (durationMatch) {
            durationValue = durationMatch[1]; // Numeric part
            durationUnit = durationMatch[2].trim() || "Days"; // Text part or default to "Days"

            // Make sure the first letter is capitalized for consistency
            durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();

            // Normalize to one of our standard units
            if (["Day", "day", "days"].includes(durationUnit)) {
              durationUnit = "Days";
            } else if (["Week", "week", "weeks"].includes(durationUnit)) {
              durationUnit = "Weeks";
            } else if (["Month", "month", "months"].includes(durationUnit)) {
              durationUnit = "Months";
            } else if (["Year", "year", "years"].includes(durationUnit)) {
              durationUnit = "Years";
            }
          }
        }

        return {
          ...diagnosis,
          durationValue,
          durationUnit
        };
      });

      // Parse and prepare prescription to handle duration properly
     const parsedPrescription = (visitToEdit.prescription ? sanitizeData(visitToEdit.prescription) : []).map(med => {
  // Parse duration value and unit if duration exists
  let durationValue = "";
  let durationUnit = "Days"; // Default unit

  if (med.duration) {
    // RegEx to extract numeric value and text unit from duration string
    const durationMatch = med.duration.match(/^(\d+)\s*(.*)$/);
    if (durationMatch) {
      durationValue = durationMatch[1]; // Numeric part
      durationUnit = durationMatch[2].trim() || "Days"; // Text part or default to "Days"

      // Make sure the first letter is capitalized for consistency
      durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();

      // Normalize to one of our standard units
      if (["Day", "day", "days"].includes(durationUnit)) {
        durationUnit = "Days";
      } else if (["Week", "week", "weeks"].includes(durationUnit)) {
        durationUnit = "Weeks";
      } else if (["Month", "month", "months"].includes(durationUnit)) {
        durationUnit = "Months";
      } else if (["Year", "year", "years"].includes(durationUnit)) {
        durationUnit = "Years";
      }
    }
  }

  return {
    ...med,
    durationValue,
    durationUnit,
    visitId: visitToEdit.visitId // Add visitId to track which visit this belongs to
  };
});

//Get medicines from medicine list for this specific visit
// NOTE: Not combining with visitMedicines to avoid duplicate medicines
// The prescription array already contains all medicines from the OPVisit
const allPrescriptions = [...parsedPrescription];

      let nextVisitDurationValue = "";
      let nextVisitDurationUnit = "Days";

      if (visitToEdit.nextVisitDuration) {
        const durationMatch = visitToEdit.nextVisitDuration.match(/^(\d+)\s*(.*)$/);
        if (durationMatch) {
          nextVisitDurationValue = durationMatch[1];
          nextVisitDurationUnit = durationMatch[2].trim() || "Days";

          // Normalize unit names
          if (["Day", "day", "days"].includes(nextVisitDurationUnit)) {
            nextVisitDurationUnit = "Days";
          } else if (["Week", "week", "weeks"].includes(nextVisitDurationUnit)) {
            nextVisitDurationUnit = "Weeks";
          } else if (["Month", "month", "months"].includes(nextVisitDurationUnit)) {
            nextVisitDurationUnit = "Months";
          }
        }
      }

      // Modify the getCurrentDate function to be more direct
      const getCurrentDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
     
      

      // Function to ensure at least one empty row exists
      const ensureEmptyRow = (dataArray, emptyRowTemplate) => {
        if (!dataArray.some(row => Object.values(row).every(val => val === "" || val === null || val === undefined))) {
          dataArray.push(emptyRowTemplate);
        }
        return dataArray;
      };

      // Ensure secondary diagnosis has at least one empty row
      let secondaryDiagnosis = parsedSecondaryDiagnosis;
      secondaryDiagnosis = ensureEmptyRow(secondaryDiagnosis, {
        diagnosis: "",
        duration: "",
        comments: "",
        date: "",  // UPDATED: Removed default date for empty row
        durationValue: "",
        durationUnit: "Days"
      });

      // Ensure prescription has at least one empty row
      let prescription = allPrescriptions;
      prescription = ensureEmptyRow(prescription, {
        medicine: "",
        dose: "",
        when: "",
        duration: "",
        quantity: "",
        unit: "",
        instructions: "",
        durationValue: "",
        durationUnit: "Days"
      });

      // Ensure treatment has at least one empty row
      let treatment = sanitizeData(visitToEdit.treatment || []);
      treatment = ensureEmptyRow(treatment, {
        treatmentType: "",
        description: "",
        date: ""  // UPDATED: Removed default date for empty row
      });

      // Ensure complaints has at least one empty row with duration handling
      let complaints = parsedComplaints.length > 0 ? parsedComplaints : [];
      complaints = ensureEmptyRow(complaints, {
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        date: "",  // UPDATED: Removed default date for empty row
        durationValue: "",
        durationUnit: "Days"
      });

      // Set the form data with the processed data
      setFormData({
        ...visitToEdit,
        vitals: vitalsArray,
        complaints: complaints,
        secondaryDiagnosis: secondaryDiagnosis,
        prescription: prescription, // This will now include both sources
        treatment: treatment,
        nextVisitDate: visitToEdit.nextVisitDate || "",
        nextVisitDuration: visitToEdit.nextVisitDuration || "",
        nextVisitDurationValue: nextVisitDurationValue,
        nextVisitDurationUnit: nextVisitDurationUnit
      });

      setCurrentEditIndex(visitIndex);
    } else {
      // Add mode - reset form with current date and default empty rows
      const today = new Date();
      const formattedDate = formatDateForInput(today);
      const formattedTime = formatTimeForInput(today);

      // Reset form data with default values and ensure empty rows
      setFormData({
        visitDate: formattedDate,
        visitTime: formattedTime,
        vitals: [{
          height: "",
          weight: "",
          pulse: "",
          temperature: "",
          bp: "",
          spo2: "",
          respiratoryRate: "",
          bloodGlucose: "",
          glucoseTestType: "",
          bsa: "",
          bmi: "",
          recordTime: formattedTime,
          sequenceNumber: 1
        }],
        complaints: [{
          complaint: "",
          frequency: "",
          severity: "",
          duration: "",
          date: "",  // UPDATED: Removed default date
          durationValue: "",
          durationUnit: "Days"
        }],
        historyOfPresentIllness: "",
        clinicalAssessment: {
          statusOfDisease: "",
          ecog: "",
          kps: "",
          onExamination: "",
        },
        secondaryDiagnosis: [{
          diagnosis: "",
          duration: "",
          comments: "",
          date: "",  // UPDATED: Removed default date
          durationValue: "",
          durationUnit: "Days"
        }],
        prescription: [{
          medicine: "",
          dose: "",
          when: "",
          duration: "",
          quantity: "",
          unit: "",
          instructions: "",
          durationValue: "",
          durationUnit: "Days"
        }],
        treatment: [{
          treatmentType: "",
          description: "",
          date: ""  // UPDATED: Removed default date
        }],
        adviceNotes: "",
        testsRequested: "",
        reviewNotes: "",
        referTo: "",
        nextVisitDate: "",
        nextVisitDuration: "",
        nextVisitDurationValue: "",
        nextVisitDurationUnit: "Days"
      });

      setCurrentEditIndex(null);
    }

    setIsFormVisible(true);
    setErrors({});
  };

  const handleDropdownToggle = (type, index, field) => {
    if (activeDropdown.type === type && activeDropdown.index === index && activeDropdown.field === field) {
      // Close dropdown if clicking the same field
      setActiveDropdown({ type: null, index: null, field: null });
    } else {
      // Open this dropdown, close others
      setActiveDropdown({ type, index, field });
    }
  };


  // Updated handler for duration unit selection
  const handleDurationUnitSelect = (index, selectedUnit) => {

    const updatedComplaints = [...formData.complaints];
    const durationValue = updatedComplaints[index].durationValue || "";

    updatedComplaints[index] = {
      ...updatedComplaints[index],
      durationUnit: selectedUnit,
      duration: durationValue ? `${durationValue} ${selectedUnit}` : ""
    };

    setFormData(prevData => ({
      ...prevData,
      complaints: updatedComplaints
    }));
  };

  // Updated handler for complaint changes
  const handleComplaintChange = (index, field, value) => {
    const updatedComplaints = [...formData.complaints];

    if (field === "durationValue") {
      const numericValue = value.replace(/[^0-9]/g, '');
      const currentUnit = updatedComplaints[index].durationUnit || "Days";

      updatedComplaints[index] = {
        ...updatedComplaints[index],
        durationValue: numericValue,
        duration: numericValue ? `${numericValue} ${currentUnit}` : ""
      };
    } else {
      updatedComplaints[index] = {
        ...updatedComplaints[index],
        [field]: value
      };
    }

    // Check if the last row is being filled and add a new row if needed
    if (index === updatedComplaints.length - 1 && isComplaintRowFilled(index)) {
      updatedComplaints.push({
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        date: "",  // UPDATED: Removed default date from new row
        durationValue: "",
        durationUnit: "Days"
      });
    }

    setFormData(prevData => ({
      ...prevData,
      complaints: updatedComplaints
    }));
  };



  // Hide OP Visit form
  const hideOPVisitForm = () => {
    setIsFormVisible(false);
    setCurrentEditIndex(null);
    setErrors({});
    // Reset form data to initial state
    setFormData(initializeFormData());
    // Clear any selected tests
    setSelectedTests([]);
    hasUserEnteredData();
  };

  // Handle form input changes
  // const handleInputChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     [name]: type === "checkbox" ? checked : value,
  //   }));
  // };
// Update the handler for visit date and next visit date
const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;
  
  setFormData((prevData) => {
    // Special handling for date fields
    if (name === 'visitDate' || name === 'nextVisitDate') {
      return {
        ...prevData,
        [name]: value  // Keep input as YYYY-MM-DD
      };
    }

    // Handle nested date fields in arrays
    if (name.includes('complaints') || name.includes('secondaryDiagnosis') || name.includes('treatment')) {
      const [arrayName, index, fieldName] = name.split('.');
      
      const updatedArray = [...prevData[arrayName]];
      if (fieldName === 'date') {
        updatedArray[index] = {
          ...updatedArray[index],
          [fieldName]: value  // Keep input as YYYY-MM-DD
        };
      } else {
        updatedArray[index] = {
          ...updatedArray[index],
          [fieldName]: value
        };
      }

      return {
        ...prevData,
        [arrayName]: updatedArray
      };
    }

    // Default handling
    return {
      ...prevData,
      [name]: type === "checkbox" ? checked : value
    };
  });
};

  // Handler for next visit duration value change
  const handleNextVisitDurationChange = (value) => {
    const currentUnit = formData.nextVisitDurationUnit || "Days";

    setFormData(prevData => ({
      ...prevData,
      nextVisitDurationValue: value,
      nextVisitDuration: value ? `${value} ${currentUnit}` : ""
    }));
  };

  // Handler for next visit duration unit selection
  const handleNextVisitDurationUnitSelect = (selectedUnit) => {
    const currentValue = formData.nextVisitDurationValue || "";

    setFormData(prevData => ({
      ...prevData,
      nextVisitDuration: currentValue ? `${currentValue} ${selectedUnit}` : ""
    }));
  };

  // Handle vitals input changes
  const handleVitalsChange = (e, vitalIndex) => {
    const { name, value } = e.target;
    
    // Update the form data with the new value
    setFormData((prevData) => {
      const updatedVitals = [...prevData.vitals];
      updatedVitals[vitalIndex] = {
        ...updatedVitals[vitalIndex],
        [name]: value,
      };
      return {
        ...prevData,
        vitals: updatedVitals,
      };
    });
    
    // If the changed field is height or weight, update the patient table
    if (name === 'height' || name === 'weight') {
      // Use setTimeout to ensure formData has been updated before we access it
      setTimeout(() => {
        // Get the current vital record after state update
        const currentVital = formData.vitals[vitalIndex];
        
        // For the field that just changed, use the new value from the event
        // For the other field, get it from the current form data
        const heightValue = name === 'height' ? value : currentVital.height;
        const weightValue = name === 'weight' ? value : currentVital.weight;
        
        // Only update if at least one of the values is present
        if ((heightValue && heightValue.trim() !== '') || (weightValue && weightValue.trim() !== '')) {
          
          // Call the updatePatientHeightWeight function to update the patient table
          updatePatientHeightWeight(heightValue, weightValue)
            .then(result => {
              if (result.success) {
              } else {
              }
            })
            .catch(error => {
            });
        }
      }, 100); // Small timeout to ensure formData is updated
    }
    
    // If height or weight changes, calculate BMI and BSA
    if (name === 'height' || name === 'weight') {
      setTimeout(() => calculateBMIAndBSA(vitalIndex), 100);
    }
  };

  const handleSecondaryDiagnosisDurationUnitSelect = (index, selectedUnit) => {
    const updatedDiagnoses = [...formData.secondaryDiagnosis];
    const durationValue = updatedDiagnoses[index].durationValue || "";

    updatedDiagnoses[index] = {
      ...updatedDiagnoses[index],
      durationUnit: selectedUnit,
      duration: durationValue ? `${durationValue} ${selectedUnit}` : ""
    };

    setFormData(prevData => ({
      ...prevData,
      secondaryDiagnosis: updatedDiagnoses
    }));
  };
  const addVitalsRecord = () => {
    const now = new Date();
    const formattedTime = formatTimeForInput(now);

    setFormData((prevData) => {
      const newSequenceNumber = prevData.vitals.length + 1;
      return {
        ...prevData,
        vitals: [
          ...prevData.vitals,
          {
            height: "",
            weight: "",
            pulse: "",
            temperature: "",
            bp: "",
            spo2: "",
            respiratoryRate: "",
            bloodGlucose: "",
            glucoseTestType: "",
            bsa: "",
            bmi: "",
            recordTime: formattedTime,
            sequenceNumber: newSequenceNumber, // Set the sequence number for the new record
          }
        ],
      };
    });
  };

  const removeVitalsRecord = (index) => {
    if (formData.vitals.length <= 1) {
      // Don't remove if it's the last vitals record
      return;
    }

    setFormData((prevData) => {
      const updatedVitals = prevData.vitals.filter((_, idx) => idx !== index);

      // Re-sequence the remaining vitals records
      const resequencedVitals = updatedVitals.map((vital, idx) => ({
        ...vital,
        sequenceNumber: idx + 1,
      }));

      return {
        ...prevData,
        vitals: resequencedVitals,
      };
    });
  };

  // Handle clinical assessment changes
  const handleClinicalAssessmentChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      clinicalAssessment: {
        ...prevData.clinicalAssessment,
        [name]: value,
      },
    }));
  };

  const isComplaintRowFilled = (index) => {
    const complaint = formData.complaints[index];
    return complaint.complaint || complaint.frequency || complaint.severity || complaint.duration;
  };

  // Add complaint row
  const addComplaintRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      complaints: [...prevData.complaints, { complaint: "", frequency: "", severity: "", duration: "", date: getCurrentDate() }],
    }));
  };

  // Remove complaint row
  const removeComplaintRow = (index) => {
    const updatedComplaints = [...formData.complaints];
    updatedComplaints.splice(index, 1);

    if (updatedComplaints.length === 0) {
      updatedComplaints.push({ complaint: "", frequency: "", severity: "", duration: "", date: getCurrentDate() });
    }

    setFormData((prevData) => ({
      ...prevData,
      complaints: updatedComplaints,
    }));
  };

  // Check if a diagnosis row is filled
  const isDiagnosisRowFilled = (index) => {
    const diagnosis = formData.secondaryDiagnosis[index];
    return diagnosis.diagnosis || diagnosis.duration || diagnosis.comments;
  };



  // Updated handler for secondary diagnosis changes
  // Update the handleSecondaryDiagnosisChange function
  const handleSecondaryDiagnosisChange = (index, field, value) => {
    const updatedDiagnoses = [...formData.secondaryDiagnosis];

    if (field === "durationValue") {
      const numericValue = value.replace(/[^0-9]/g, '');
      const currentUnit = updatedDiagnoses[index].durationUnit || "Days";

      updatedDiagnoses[index] = {
        ...updatedDiagnoses[index],
        durationValue: numericValue,
        duration: numericValue ? `${numericValue} ${currentUnit}` : ""
      };
    } else {
      updatedDiagnoses[index] = {
        ...updatedDiagnoses[index],
        [field]: value
      };
    }

    // Check if the last row is being filled and add a new row if needed
    if (index === updatedDiagnoses.length - 1 && isDiagnosisRowFilled(index)) {
      updatedDiagnoses.push({
        diagnosis: "",
        duration: "",
        comments: "",
        date: "",  // UPDATED: Removed default date from new row
        durationValue: "",
        durationUnit: "Days"
      });
    }

    setFormData(prevData => ({
      ...prevData,
      secondaryDiagnosis: updatedDiagnoses
    }));
  };

  // Update when props change
  useEffect(() => {
    if (assessmentId) {
      setCurrentAssessmentId(assessmentId);
    }
  }, [assessmentId]);
  // Add secondary diagnosis row
  const addSecondaryDiagnosisRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      secondaryDiagnosis: [...prevData.secondaryDiagnosis, { diagnosis: "", duration: "", comments: "", date: getCurrentDate() }],
    }));
  };

  // Remove secondary diagnosis row
  const removeSecondaryDiagnosisRow = (index) => {
    const updatedDiagnoses = [...formData.secondaryDiagnosis];
    updatedDiagnoses.splice(index, 1);

    if (updatedDiagnoses.length === 0) {
      updatedDiagnoses.push({ diagnosis: "", duration: "", comments: "", date: getCurrentDate() });
    }

    setFormData((prevData) => ({
      ...prevData,
      secondaryDiagnosis: updatedDiagnoses,
    }));
  };

  const isPrescriptionRowFilled = (index) => {
    const med = formData.prescription[index];
    return med.medicine || med.dose || med.when || med.durationValue || med.durationUnit || med.quantity || med.unit || med.instructions;
  };

  // Function to handle duration unit selection for prescription
  const handlePrescriptionDurationUnitSelect = (index, selectedUnit) => {
    setFormData(prevData => {
      const updatedPrescriptions = [...prevData.prescription];
      const durationValue = updatedPrescriptions[index]?.durationValue || "";

      updatedPrescriptions[index] = {
        ...updatedPrescriptions[index],
        durationUnit: selectedUnit,
        duration: durationValue ? `${durationValue} ${selectedUnit}` : "",
        isModified: true // Mark as modified
      };

      return {
        ...prevData,
        prescription: updatedPrescriptions
      };
    });
  };

  // Handle prescription changes
  // 6. UPDATE the handlePrescriptionChange function to remove xref handling:
const handlePrescriptionChange = (index, field, value) => {
  const updatedPrescriptions = [...formData.prescription];

  if (field === "durationValue") {
    const numericValue = value.replace(/[^0-9]/g, '');
    const currentUnit = updatedPrescriptions[index].durationUnit || "Days";

    updatedPrescriptions[index] = {
      ...updatedPrescriptions[index],
      durationValue: numericValue,
      duration: numericValue ? `${numericValue} ${currentUnit}` : "",
      isModified: true
    };
  } else {
    updatedPrescriptions[index] = {
      ...updatedPrescriptions[index],
      [field]: value,
      isModified: true
    };
  }

  // Check if the last row is being filled and add a new row if needed
  if (index === updatedPrescriptions.length - 1 && isPrescriptionRowFilled(index)) {
    updatedPrescriptions.push({
      medicine: "",
      dose: "",
      when: "",
      duration: "",
      quantity: "",
      unit: "",
      instructions: "",
      durationValue: "",
      durationUnit: "Days"
    });
  }

  setFormData(prevData => ({
    ...prevData,
    prescription: updatedPrescriptions
  }));
};


  // Add prescription row
  const addPrescriptionRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      prescription: [...prevData.prescription, { medicine: "", dose: "", when: "", duration: "", quantity: "", unit: "", instructions: "" }],
    }));
  };

  // Remove prescription row
  const removePrescriptionRow = (index) => {
  const updatedPrescriptions = [...formData.prescription];
  updatedPrescriptions.splice(index, 1);

  if (updatedPrescriptions.length === 0) {
    updatedPrescriptions.push({ 
      medicine: "", 
      dose: "", 
      when: "", 
      duration: "", 
      quantity: "", 
      unit: "", 
      instructions: "",
      durationValue: "",
      durationUnit: "Days"
    });
  }

  setFormData((prevData) => ({
    ...prevData,
    prescription: updatedPrescriptions,
  }));
};

  const handleTestSelection = (test) => {
    if (selectedTests.includes(test)) {
      setSelectedTests(selectedTests.filter(t => t !== test));
    } else {
      setSelectedTests([...selectedTests, test]);
    }
  };

  // Handle removing a test
  const handleRemoveTest = (test) => {
    setSelectedTests(selectedTests.filter(t => t !== test));
  };



  useEffect(() => {
    if (selectedTests.length > 0) {
      setFormData(prevData => ({
        ...prevData,
        testsRequested: selectedTests.join(', ')
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        testsRequested: ''
      }));
    }
  }, [selectedTests]);

  // Parse existing testsRequested value when editing
  useEffect(() => {
    if (formData.testsRequested) {
      setSelectedTests(formData.testsRequested.split(', ').filter(test => test.trim() !== ''));
    } else {
      setSelectedTests([]);
    }
  }, [formData.testsRequested]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown.type &&
        !event.target.closest('.dropdown-container') &&
        !event.target.closest('.duration-dropdown-menu')) {
        setActiveDropdown({ type: null, index: null, field: null });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Function to save the current selection to a quick list
  const handleSaveToTestList = () => {
    swal({
      title: "Success!",
      text: "Tests saved to quick list",
      icon: "success",
      buttons: false,
      timer: 1500,
    });
  };

  // Optionally, fetch test options from API
  const fetchTestOptions = async () => {
    try {

      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllTestsRequestedMaster`);
      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result && result.message) {
        try {
          const data = JSON.parse(result.message);

          if (Array.isArray(data)) {
            // Extract test names correctly based on API response structure
            // Using testsRequestedName from the response
            const formattedOptions = data.map(test => test.testsRequestedName || "");

            // Filter out empty strings and duplicates
            const filteredOptions = [...new Set(formattedOptions)]
              .filter(name => name.trim() !== "")
              .sort((a, b) => a.localeCompare(b));


            if (filteredOptions.length > 0) {
              setTestOptions(filteredOptions);
            } else {
            }
          } else {
          }
        } catch (parseErr) {
        }
      } else {
      }
    } catch (err) {
    }
  };

  // Updated fetchPatientMedicines function to handle medicine list
  const fetchPatientMedicines = async (patientId) => {
  try {
    // Set loading state
    setMedicinesFetchStatus('loading');
    setMedicinesFetchError(null);
    

    const response = await fetch(`${C.getUrl()}/medicineListXref/getPatientMedicines?patientId=${patientId}`);
    
    // Check if response is OK
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();

    // Bail out if the component unmounted while the request was in flight.
    if (!isMountedRef.current) return;

    // Handle different status codes
    if ((data.statusCode === 200 || data.statusCode === 451) && data.message) {
      try {
        // 451 = No active medicines found (normal for new patients)
        // 200 = Medicines found
        
        if (data.statusCode === 451) {
          // No medicines prescribed yet - this is normal for newly registered patients
          setMedicinesByVisit({});
          setMedicinesFetchStatus('success');
        } else {
          const medicines = JSON.parse(data.message);

          if (Array.isArray(medicines) && medicines.length > 0) {
            // Group medicines by visitId
            const medicinesByVisitId = {};
            
            medicines.forEach(med => {
              const visitId = med.visitId;
              if (!medicinesByVisitId[visitId]) {
                medicinesByVisitId[visitId] = [];
              }
              
              medicinesByVisitId[visitId].push({
                medicine: med.medicineName,
                dose: med.dose,
                when: med.when,
                duration: med.duration,
                quantity: med.quantity || "",
                unit: med.unit || "",
                instructions: med.instructions,
                medicineListXrefId: med.medicineListXrefId,
                visitId: med.visitId,
                isFromMedicineList: true // Flag to identify these medicines
              });
            });

            // Update state with grouped medicines
            setMedicinesByVisit(medicinesByVisitId);
            setMedicinesFetchStatus('success');
            
          } else {
            // No medicines found but API succeeded
            setMedicinesByVisit({});
            setMedicinesFetchStatus('success');
          }
        }
      } catch (parseError) {
        const errorMsg = `Error parsing medicine data: ${parseError.message}`;
        setMedicinesFetchStatus('error');
        setMedicinesFetchError(errorMsg);
      }
    } else {
      const errorMsg = `API Error: ${data.message || 'Unknown error'}`;
      setMedicinesFetchStatus('error');
      setMedicinesFetchError(errorMsg);
    }
  } catch (error) {
    if (!isMountedRef.current) return;
    const errorMsg = `Error fetching patient medicines: ${error.message}`;
    setMedicinesFetchStatus('error');
    setMedicinesFetchError(errorMsg);
  }
};

//get medicines for a specific visit
const getMedicinesForVisit = (visitId) => {
  const visitMedicines = medicinesByVisit[visitId] || [];
  return visitMedicines;
};


  // Function to delete a patient medicine
  const deletePatientMedicine = async (medicineListXrefId) => {
    try {
      // Show confirmation dialog before deletion
      const willDelete = await new Promise(resolve => {
        swal({
          title: "Are you sure?",
          text: "Once deleted, you will not be able to recover this prescription!",
          icon: "warning",
          buttons: true,
          dangerMode: true,
        })
          .then(willDelete => {
            resolve(willDelete);
          });
      });

      if (!willDelete) {
        return false;
      }

      // Show loading indicator
      setSaveLoader(true);

      // Make the API call to delete the medicine
      const response = await fetch(`${C.getUrl()}/medicineListXref/deactivate/${medicineListXrefId}`, {
        method: 'PUT'
      });

      const result = await response.json();

      if (result.statusCode === 200 || result.serviceStatus === "SUCCESS") {
        // Show success message
        swal({
          title: "Success!",
          text: "Medicine deleted successfully",
          icon: "success",
          buttons: false,
          timer: 1500,
        });

        // Refresh the medicine list
        const patientId = localStorage.getItem("PatientpatientId");
        await fetchPatientMedicines(patientId);

        return true;
      } else {
        throw new Error(result.message || "Failed to delete medicine");
      }
    } catch (error) {

      swal({
        title: "Error!",
        text: error.message || "Failed to delete medicine",
        icon: "error",
        buttons: false,
        timer: 2000,
      });

      return false;
    } finally {
      setSaveLoader(false);
    }
  };


  useEffect(() => {
    fetchTestOptions();
  }, []);


  useEffect(() => {
    if (selectedTests.length > 0) {
      setFormData(prevData => ({
        ...prevData,
        testsRequested: selectedTests.join(', ')
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        testsRequested: ''
      }));
    }
  }, [selectedTests]);

  const isTreatmentRowFilled = (index) => {
    const treatment = formData.treatment[index];
    return treatment.treatmentType || treatment.description;
  };


  // Handle treatment changes
  const handleTreatmentChange = (index, field, value) => {
    const updatedTreatments = [...formData.treatment];
    updatedTreatments[index] = {
      ...updatedTreatments[index],
      [field]: value,
    };

    // Check if the last row is being filled and add a new row if needed
    if (index === updatedTreatments.length - 1 && isTreatmentRowFilled(index)) {
      updatedTreatments.push({
        treatmentType: "",
        description: "",
        date: ""  // UPDATED: Removed default date from new row
      });
    }

    setFormData((prevData) => ({
      ...prevData,
      treatment: updatedTreatments,
    }));
  };

  // Add treatment row
  const addTreatmentRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      treatment: [...prevData.treatment, { treatmentType: "", description: "", date: getCurrentDate() }],
    }));
  };

  // Remove treatment row
  const removeTreatmentRow = (index) => {
    const updatedTreatments = [...formData.treatment];
    updatedTreatments.splice(index, 1);

    if (updatedTreatments.length === 0) {
      updatedTreatments.push({ treatmentType: "", description: "", date: getCurrentDate() });
    }

    setFormData((prevData) => ({
      ...prevData,
      treatment: updatedTreatments,
    }));
  };


  // Load previous data (placeholder function)
  const loadPrevious = (section) => {
    if (!opVisits || opVisits.length === 0) {
      swal({
        title: "No previous data",
        text: "There are no previous visits to load data from.",
        icon: "info",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // Get the most recent visit (excluding the one being edited if in edit mode)
    let mostRecentVisit;
    if (currentEditIndex !== null) {
      // Filter out the current visit being edited
      const otherVisits = opVisits.filter((_, index) => index !== currentEditIndex);
      if (otherVisits.length === 0) {
        swal({
          title: "No previous data",
          text: "There are no other visits to load data from.",
          icon: "info",
          buttons: false,
          timer: 2000,
        });
        return;
      }
      // Sort visits by date (newest first)
      mostRecentVisit = [...otherVisits].sort((a, b) =>
        new Date(b.visitDate) - new Date(a.visitDate)
      )[0];
    } else {
      // Sort all visits by date (newest first)
      mostRecentVisit = [...opVisits].sort((a, b) =>
        new Date(b.visitDate) - new Date(a.visitDate)
      )[0];
    }

    // Load data based on the section
    switch (section) {
      case 'complaints':
        loadPreviousComplaints(mostRecentVisit);
        break;
      case 'history':
        loadPreviousHistory(mostRecentVisit);
        break;
      case 'assessment':
        loadPreviousClinicalAssessment(mostRecentVisit);
        break;
      case 'secondaryDiagnosis':
        loadPreviousSecondaryDiagnosis(mostRecentVisit);
        break;
      case 'prescription':
        loadPreviousPrescription(mostRecentVisit);
        break;
      case 'treatment':
        loadPreviousTreatment(mostRecentVisit);
        break;
      default:
    }
  };

  // Function to load previous complaints
  // const loadPreviousComplaints = (visit) => {
  //   if (!visit) return;

  //   // Get complaints from the most recent visit
  //   const previousComplaints = visit.complaints ||
  //     (visit.complaintsList ? visit.complaintsList.map(c => ({
  //       complaint: c.complaint,
  //       frequency: c.frequency,
  //       severity: c.severity,
  //       duration: c.duration,
  //       date: c.complaintDate
  //     })) : []);

  //   if (!previousComplaints || previousComplaints.length === 0) {
  //     swal({
  //       title: "No previous data",
  //       text: "No complaint data found in the previous visit.",
  //       icon: "info",
  //       buttons: false,
  //       timer: 2000,
  //     });
  //     return;
  //   }

  //   // Load data without confirmation
  //   // If there are already complaints filled out, add an empty one at the end for future entries
  //   const updatedComplaints = [...previousComplaints];

  //   // Add an empty complaint row at the end for new entries
  //   if (!updatedComplaints.some(c => !c.complaint && !c.frequency && !c.severity && !c.duration && !c.date)) {
  //     updatedComplaints.push({ complaint: "", frequency: "", severity: "", duration: "", date: "" });
  //   }

  //   setFormData(prevData => ({
  //     ...prevData,
  //     complaints: updatedComplaints
  //   }));

  //   swal({
  //     title: "Success!",
  //     text: "Previous complaints loaded successfully.",
  //     icon: "success",
  //     buttons: false,
  //     timer: 1500,
  //   });
  // };
  // 1. First, let's fix the loadPreviousComplaints function to properly handle duration values:

const loadPreviousComplaints = (visit) => {
  if (!visit) return;

  // Get complaints from the most recent visit
  const previousComplaints = visit.complaints ||
    (visit.complaintsList ? visit.complaintsList.map(c => ({
      complaint: c.complaint,
      frequency: c.frequency,
      severity: c.severity,
      duration: c.duration,
      date: c.complaintDate
    })) : []);

  if (!previousComplaints || previousComplaints.length === 0) {
    swal({
      title: "No previous data",
      text: "No complaint data found in the previous visit.",
      icon: "info",
      buttons: false,
      timer: 2000,
    });
    return;
  }

  // Process complaints to extract duration values and units
  const processedComplaints = previousComplaints.map(complaint => {
    // Parse duration value and unit if duration exists
    let durationValue = "";
    let durationUnit = "Days"; // Default unit

    if (complaint.duration) {
      // RegEx to extract numeric value and text unit from duration string
      const durationMatch = complaint.duration.match(/^(\d+)\s*(.*)$/);
      if (durationMatch) {
        durationValue = durationMatch[1]; // Numeric part
        durationUnit = durationMatch[2].trim() || "Days"; // Text part or default to "Days"

        // Make sure the first letter is capitalized for consistency
        durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();

        // Normalize to one of our standard units
        if (["Day", "day", "days"].includes(durationUnit)) {
          durationUnit = "Days";
        } else if (["Week", "week", "weeks"].includes(durationUnit)) {
          durationUnit = "Weeks";
        } else if (["Month", "month", "months"].includes(durationUnit)) {
          durationUnit = "Months";
        } else if (["Year", "year", "years"].includes(durationUnit)) {
          durationUnit = "Years";
        }
      }
    }

    return {
      ...complaint,
      durationValue,
      durationUnit
    };
  });

  // If there are already complaints filled out, add an empty one at the end for future entries
  const updatedComplaints = [...processedComplaints];

  // Add an empty complaint row at the end for new entries
  if (!updatedComplaints.some(c => !c.complaint && !c.frequency && !c.severity && !c.duration && !c.date)) {
    updatedComplaints.push({ complaint: "", frequency: "", severity: "", duration: "", date: "", durationValue: "", durationUnit: "Days" });
  }

  setFormData(prevData => ({
    ...prevData,
    complaints: updatedComplaints
  }));

  swal({
    title: "Success!",
    text: "Previous complaints loaded successfully.",
    icon: "success",
    buttons: false,
    timer: 1500,
  });
};

  // Function to load previous history
  const loadPreviousHistory = (visit) => {
    if (!visit) return;

    // Get history from the most recent visit
    const previousHistory = visit.historyOfPresentIllness ||
      (visit.presentIllnessList && visit.presentIllnessList.length > 0 ? visit.presentIllnessList[0].historyText : "");

    if (!previousHistory) {
      swal({
        title: "No previous data",
        text: "No history data found in the previous visit.",
        icon: "info",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // Load data without confirmation
    setFormData(prevData => ({
      ...prevData,
      historyOfPresentIllness: previousHistory
    }));

    swal({
      title: "Success!",
      text: "Previous history loaded successfully.",
      icon: "success",
      buttons: false,
      timer: 1500,
    });
  };

  const loadPreviousClinicalAssessment = (visit) => {
    if (!visit) return;

    // Get clinical assessment from the most recent visit
    const previousAssessment = visit.clinicalAssessment || {};
    const apiAssessment = visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0
      ? visit.clinicalAssessmentList[0] : {};

    const statusOfDisease = previousAssessment.statusOfDisease || apiAssessment.diseaseStatus || "";
    const ecog = previousAssessment.ecog || apiAssessment.ecog || "";
    const kps = previousAssessment.kps || apiAssessment.kps || "";
    const onExamination = previousAssessment.onExamination || apiAssessment.examinationNotes || "";

    if (!statusOfDisease && !ecog && !kps && !onExamination) {
      swal({
        title: "No previous data",
        text: "No clinical assessment data found in the previous visit.",
        icon: "info",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // Load data without confirmation
    setFormData(prevData => ({
      ...prevData,
      clinicalAssessment: {
        statusOfDisease,
        ecog,
        kps,
        onExamination
      }
    }));

    swal({
      title: "Success!",
      text: "Previous clinical assessment loaded successfully.",
      icon: "success",
      buttons: false,
      timer: 1500,
    });
  };

  // // Function to load previous secondary diagnosis
  // const loadPreviousSecondaryDiagnosis = (visit) => {
  //   if (!visit) return;

  //   // Get secondary diagnosis from the most recent visit
  //   const previousDiagnoses = visit.secondaryDiagnosis ||
  //     (visit.secondaryDiagnosisList ? visit.secondaryDiagnosisList.map(d => ({
  //       diagnosis: d.diagnosis,
  //       duration: d.duration,
  //       comments: d.comments,
  //       date: d.diagnosisDate
  //     })) : []);

  //   if (!previousDiagnoses || previousDiagnoses.length === 0) {
  //     swal({
  //       title: "No previous data",
  //       text: "No secondary diagnosis data found in the previous visit.",
  //       icon: "info",
  //       buttons: false,
  //       timer: 2000,
  //     });
  //     return;
  //   }

  //   // Load data without confirmation
  //   // If there are already diagnoses filled out, add an empty one at the end for future entries
  //   const updatedDiagnoses = [...previousDiagnoses];

  //   // Add an empty diagnosis row at the end for new entries
  //   if (!updatedDiagnoses.some(d => !d.diagnosis && !d.duration && !d.comments && !d.date)) {
  //     updatedDiagnoses.push({ diagnosis: "", duration: "", comments: "", date: "" });
  //   }

  //   setFormData(prevData => ({
  //     ...prevData,
  //     secondaryDiagnosis: updatedDiagnoses
  //   }));

  //   swal({
  //     title: "Success!",
  //     text: "Previous secondary diagnoses loaded successfully.",
  //     icon: "success",
  //     buttons: false,
  //     timer: 1500,
  //   });
  // };
  // 2. Now, let's fix the loadPreviousSecondaryDiagnosis function:

const loadPreviousSecondaryDiagnosis = (visit) => {
  if (!visit) return;

  // Get secondary diagnosis from the most recent visit
  const previousDiagnoses = visit.secondaryDiagnosis ||
    (visit.secondaryDiagnosisList ? visit.secondaryDiagnosisList.map(d => ({
      diagnosis: d.diagnosis,
      duration: d.duration,
      comments: d.comments,
      date: d.diagnosisDate
    })) : []);

  if (!previousDiagnoses || previousDiagnoses.length === 0) {
    swal({
      title: "No previous data",
      text: "No secondary diagnosis data found in the previous visit.",
      icon: "info",
      buttons: false,
      timer: 2000,
    });
    return;
  }

  // Process diagnoses to extract duration values and units
  const processedDiagnoses = previousDiagnoses.map(diagnosis => {
    // Parse duration value and unit if duration exists
    let durationValue = "";
    let durationUnit = "Days"; // Default unit

    if (diagnosis.duration) {
      // RegEx to extract numeric value and text unit from duration string
      const durationMatch = diagnosis.duration.match(/^(\d+)\s*(.*)$/);
      if (durationMatch) {
        durationValue = durationMatch[1]; // Numeric part
        durationUnit = durationMatch[2].trim() || "Days"; // Text part or default to "Days"

        // Make sure the first letter is capitalized for consistency
        durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();

        // Normalize to one of our standard units
        if (["Day", "day", "days"].includes(durationUnit)) {
          durationUnit = "Days";
        } else if (["Week", "week", "weeks"].includes(durationUnit)) {
          durationUnit = "Weeks";
        } else if (["Month", "month", "months"].includes(durationUnit)) {
          durationUnit = "Months";
        } else if (["Year", "year", "years"].includes(durationUnit)) {
          durationUnit = "Years";
        }
      }
    }

    return {
      ...diagnosis,
      durationValue,
      durationUnit
    };
  });

  // If there are already diagnoses filled out, add an empty one at the end for future entries
  const updatedDiagnoses = [...processedDiagnoses];

  // Add an empty diagnosis row at the end for new entries
  if (!updatedDiagnoses.some(d => !d.diagnosis && !d.duration && !d.comments && !d.date)) {
    updatedDiagnoses.push({ diagnosis: "", duration: "", comments: "", date: "", durationValue: "", durationUnit: "Days" });
  }

  setFormData(prevData => ({
    ...prevData,
    secondaryDiagnosis: updatedDiagnoses
  }));

  swal({
    title: "Success!",
    text: "Previous secondary diagnoses loaded successfully.",
    icon: "success",
    buttons: false,
    timer: 1500,
  });
};

  // Function to load previous prescription
  // const loadPreviousPrescription = (visit) => {
  //   if (!visit) return;

  //   // Get prescription from the most recent visit
  //   const previousPrescriptions = visit.prescription ||
  //     (visit.prescriptionList ? visit.prescriptionList.map(p => ({
  //       medicine: p.medicineName,
  //       dose: p.dosage,
  //       when: p.frequency,
  //       duration: p.duration,
  //       quantity: p.quantity || "",
  //       unit: p.unit || "",
  //       instructions: p.instructions
  //     })) : []);

  //   if (!previousPrescriptions || previousPrescriptions.length === 0) {
  //     swal({
  //       title: "No previous data",
  //       text: "No prescription data found in the previous visit.",
  //       icon: "info",
  //       buttons: false,
  //       timer: 2000,
  //     });
  //     return;
  //   }

  //   // Load data without confirmation
  //   // If there are already prescriptions filled out, add an empty one at the end for future entries
  //   const updatedPrescriptions = [...previousPrescriptions];

  //   // Add an empty prescription row at the end for new entries
  //   if (!updatedPrescriptions.some(p => !p.medicine && !p.dose && !p.when && !p.duration && !p.instructions)) {
  //     updatedPrescriptions.push({ medicine: "", dose: "", when: "", duration: "", quantity: "", unit: "", instructions: "" });
  //   }

  //   setFormData(prevData => ({
  //     ...prevData,
  //     prescription: updatedPrescriptions
  //   }));

  //   // Clear any validation errors
  //   setMedicationErrors([]);

  //   swal({
  //     title: "Success!",
  //     text: "Previous prescriptions loaded successfully.",
  //     icon: "success",
  //     buttons: false,
  //     timer: 1500,
  //   });
  // };
// 3. Finally, let's fix the loadPreviousPrescription function:
const loadPreviousPrescription = (visit) => {
  if (!visit) return;
  
  
  // Get prescription from the most recent visit (both sources)
  const previousPrescriptions = visit.prescription || 
    (visit.prescriptionList ? visit.prescriptionList.map(p => ({
      medicine: p.medicineName,
      dose: p.dosage,
      when: p.frequency,
      duration: p.duration,
      quantity: p.quantity || "",
      unit: p.unit || "",
      instructions: p.instructions
    })) : []);

  // ADDITION: Also get medicines from medicine list for this visit
  const visitMedicines = getMedicinesForVisit(visit.visitId) || [];
  
  // Combine both sources
  const allPreviousPrescriptions = [...previousPrescriptions];
  
  // Add medicines from medicine list
  visitMedicines.forEach(xrefMed => {
    // Check if this medicine is already in the prescription list
    const existsInPrescription = allPreviousPrescriptions.some(p => 
      p.medicine === xrefMed.medicine && p.dose === xrefMed.dose && p.when === xrefMed.when
    );
    
    if (!existsInPrescription) {
      allPreviousPrescriptions.push({
        medicine: xrefMed.medicine,
        dose: xrefMed.dose,
        when: xrefMed.when,
        duration: xrefMed.duration,
        quantity: xrefMed.quantity || "",
        unit: xrefMed.unit || "",
        instructions: xrefMed.instructions
      });
    }
  });
  
  if (!allPreviousPrescriptions || allPreviousPrescriptions.length === 0) {
    swal({
      title: "No previous data",
      text: "No prescription data found in the previous visit.",
      icon: "info",
      buttons: false,
      timer: 2000,
    });
    return;
  }
  
  // Process prescriptions to extract duration values and units
  const processedPrescriptions = allPreviousPrescriptions.map(med => {
    let durationValue = "";
    let durationUnit = "Days";
    
    if (med.duration) {
      const durationMatch = med.duration.match(/^(\d+)\s*(.*)$/);
      if (durationMatch) {
        durationValue = durationMatch[1];
        durationUnit = durationMatch[2].trim() || "Days";
        
        durationUnit = durationUnit.charAt(0).toUpperCase() + durationUnit.slice(1).toLowerCase();
        
        // Normalize units
        if (["Day", "day", "days"].includes(durationUnit.toLowerCase())) {
          durationUnit = "Days";
        } else if (["Week", "week", "weeks"].includes(durationUnit.toLowerCase())) {
          durationUnit = "Weeks";
        } else if (["Month", "month", "months"].includes(durationUnit.toLowerCase())) {
          durationUnit = "Months";
        } else if (["Year", "year", "years"].includes(durationUnit.toLowerCase())) {
          durationUnit = "Years";
        }
      }
    }
    
    return {
      ...med,
      durationValue,
      durationUnit,
      isModified: true
    };
  });
  
  // Add empty row if needed
  if (!processedPrescriptions.some(p => !p.medicine && !p.dose && !p.when && !p.duration && !p.instructions)) {
    processedPrescriptions.push({ 
      medicine: "", 
      dose: "", 
      when: "", 
      duration: "", 
      quantity: "", 
      unit: "", 
      instructions: "",
      durationValue: "",
      durationUnit: "Days"
    });
  }
  
  setFormData(prevData => ({
    ...prevData,
    prescription: processedPrescriptions
  }));
  
  setMedicationErrors([]);
  
  swal({
    title: "Success!",
    text: "Previous prescriptions loaded successfully.",
    icon: "success",
    buttons: false,
    timer: 1500,
  });
};
  // Function to load previous treatment
  const loadPreviousTreatment = (visit) => {
    if (!visit) return;

    // Get treatment from the most recent visit
    const previousTreatments = visit.treatment ||
      (visit.treatmentList ? visit.treatmentList.map(t => ({
        treatmentType: t.treatmentType,
        description: t.description,
        date: t.treatmentDate
      })) : []);

    if (!previousTreatments || previousTreatments.length === 0) {
      swal({
        title: "No previous data",
        text: "No treatment data found in the previous visit.",
        icon: "info",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // Load data without confirmation
    // If there are already treatments filled out, add an empty one at the end for future entries
    const updatedTreatments = [...previousTreatments];

    // Add an empty treatment row at the end for new entries
    if (!updatedTreatments.some(t => !t.treatmentType && !t.description && !t.date)) {
      updatedTreatments.push({ treatmentType: "", description: "", date: "" });
    }

    setFormData(prevData => ({
      ...prevData,
      treatment: updatedTreatments
    }));

    swal({
      title: "Success!",
      text: "Previous treatments loaded successfully.",
      icon: "success",
      buttons: false,
      timer: 1500,
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Add validation rules as needed
    if (!formData.visitDate) newErrors.visitDate = "Visit Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to validate prescription data
  // const validatePrescriptionData = () => {
  //   const errors = [];
  //   let isValid = true;

  //   // Only validate non-empty prescription rows
  //   formData.prescription.forEach((detail, index) => {
  //     if (!isPrescriptionRowFilled(index)) return; // Skip empty rows

  //     const rowErrors = {};

  //     if (!detail.medicine) {
  //       rowErrors.medicine = "Please select medicine";
  //       isValid = false;
  //     }

  //     if (!detail.dose) {
  //       rowErrors.dose = "Please select dose";
  //       isValid = false;
  //     }

  //     if (!detail.when) {
  //       rowErrors.when = "Please select when";
  //       isValid = false;
  //     }

  //     // Updated duration validation to check both durationValue and durationUnit
  //     if (!detail.durationValue || !detail.durationUnit) {
  //       rowErrors.duration = "Please select duration";
  //       isValid = false;
  //     }

  //     if (!detail.instructions) {
  //       rowErrors.instructions = "Please enter instructions";
  //       isValid = false;
  //     }

  //     errors[index] = Object.keys(rowErrors).length > 0 ? rowErrors : null;
  //   });

  //   setMedicationErrors(errors);
  //   return isValid;
  // };

  
  // Calculate BMI and BSA
  const calculateBMIAndBSA = (vitalIndex) => {
    const height = parseFloat(formData.vitals[vitalIndex].height);
    const weight = parseFloat(formData.vitals[vitalIndex].weight);

    if (height && weight && !isNaN(height) && !isNaN(weight)) {
      // Convert height to meters if in cm
      const heightInMeters = height / 100;

      // Calculate BMI - round to 1 decimal place
      const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(1);

      // Calculate BSA using Mosteller formula - round to 2 decimal places
      const bsa = Math.sqrt((height * weight) / 3600).toFixed(2);

      setFormData(prevData => {
        const updatedVitals = [...prevData.vitals];
        updatedVitals[vitalIndex] = {
          ...updatedVitals[vitalIndex],
          bmi,
          bsa
        };
        return {
          ...prevData,
          vitals: updatedVitals
        };
      });
    }
  };
  // Function to expose saveOPVisit to parent components
  // In OPVisitManagement component:
  useImperativeHandle(ref, () => ({
    saveOPVisit,
    hasUserEnteredData,

    // Add these methods to be called from the parent
    hideForm: () => {
      setIsFormVisible(false);
      setCurrentEditIndex(null);
      setErrors({});
      setFormData(initializeFormData());
    },

    refreshData: () => {
      fetchPatientOpVisits();
    }
  }));


  const formatTimeForSql = (timeString) => {
    // If it's empty, return null
    if (!timeString) return null;

    // If it's already in HH:MM:SS format, return as is
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }

    // If it's in HH:MM AM/PM format, convert to 24-hour format
    if (/^\d{1,2}:\d{2}\s?[APap][Mm]$/.test(timeString)) {
      const [timePart, amPmPart] = timeString.split(/\s+/);
      let [hours, minutes] = timePart.split(':').map(part => parseInt(part));

      const isPM = amPmPart.toLowerCase() === 'pm';

      if (isPM && hours < 12) {
        hours += 12;
      } else if (!isPM && hours === 12) {
        hours = 0;
      }

      // Format to HH:MM:SS with leading zeros
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }

    // If it's just HH:MM format, add seconds
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':').map(part => parseInt(part));
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }

    // If we can't parse it, return a default time
    return "00:00:00";
  };

  // Helper function to convert discharge date from dd-mm-yyyy to yyyy-mm-dd
  const convertDischargeDateForBackend = (dateString) => {
    // If it's empty, return null
    if (!dateString || dateString.trim() === "") return null;

    // If it's already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // If it's in dd-mm-yyyy format, convert to yyyy-mm-dd
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-');
      return `${year}-${month}-${day}`;
    }

    // If we can't parse it, return null
    return null;
  };

  const areVitalsValid = (vitals) => {
    const vital = vitals[0]; // Assuming only one vitals entry
  
    return vital.height?.toString().trim() !== "" && vital.weight?.toString().trim() !== "";
  };
  
  // 2. Add state for validation errors specifically for height and weight
const [vitalErrors, setVitalErrors] = useState({});
   //const [isFormValid, setIsFormValid] = useState(false);

  //  useEffect(() => {
  //   const valid = areVitalsValid(formData.vitals);
  //   setIsFormValid(valid);
  // }, [formData.vitals]);
  // console.log(opVisits);
  

  // Save OP Visit

 const saveOPVisit = async (externalAssessmentId = null) => {
  // IMMEDIATE VISUAL FEEDBACK
  
  // Reset vital errors first
  setVitalErrors({});
  
  // Check height and weight first
  const vitalValidationErrors = {};
  let hasVitalErrors = false;
  
  // Validate visitDate first
  if (!formData.visitDate || formData.visitDate.trim() === "") {
    swal({
      title: "❌ Validation Error",
      text: "Please set the Visit Date before saving",
      icon: "warning",
      buttons: {
        confirm: {
          text: "OK",
          value: true,
          visible: true,
          closeModal: true
        }
      }
    });
    return { success: false, error: "Visit Date is required" };
  }
  
  
  // Validate first vitals record (index 0)
  if (!formData.vitals[0].height || formData.vitals[0].height.trim() === "") {
    vitalValidationErrors.height = "Height is required";
    hasVitalErrors = true;
  }
  
  if (!formData.vitals[0].weight || formData.vitals[0].weight.trim() === "") {
    vitalValidationErrors.weight = "Weight is required";
    hasVitalErrors = true;
  }
  
  if (hasVitalErrors) {
    setVitalErrors(vitalValidationErrors);
    
    // Scroll to the vitals section
    const vitalsSection = document.querySelector('.vitals-section');
    if (vitalsSection) {
      vitalsSection.scrollIntoView({ behavior: 'smooth' });
    }
    
    swal({
      title: "❌ Validation Error",
      text: "Height and Weight are required",
      icon: "warning",
      buttons: {
        confirm: {
          text: "OK",
          value: true,
          visible: true,
          closeModal: true
        }
      }
    });
    
    return { success: false, error: "Height and Weight are required" };
  }

  
  // Check if there are any filled prescription rows and validate them
  // const hasFilledPrescriptions = formData.prescription.some((p, index) => isPrescriptionRowFilled(index));
  
  // // Only validate if there are filled prescription rows
  // if (hasFilledPrescriptions) {
  //   const isValid = validatePrescriptionData();
  //   if (!isValid) {
  //     swal({
  //       title: "Prescription Validation Error",
  //       text: "Please fill in all required prescription fields (Medicine, Dose, When, Duration, and Instructions)",
  //       icon: "warning",
  //       buttons: false,
  //       timer: 3000,
  //     });
  //     return { success: false, error: "Prescription validation failed" };
  //   }
  // }

  setSaveLoader(true);

  try {
    // Get patient ID from localStorage
    const patientId = localStorage.getItem("PatientpatientId");

    // Validate patient ID
    if (!patientId || isNaN(parseInt(patientId))) {
      throw new Error("Invalid patient ID. Please select a patient first.");
    }

    // Get doctor ID from localStorage
    const doctorId = localStorage.getItem("doctorId") || "1";

    // Get assessment ID from props, parameter, localStorage, or set to 0
    const assessmentId = externalAssessmentId ||
      currentAssessmentId ||
      localStorage.getItem("assessmentId") ||
      0;

    // Get current date in YYYY-MM-DD format for default values
    const currentDateStr = getCurrentDate();

    // Create vitalsList from multiple vitals records
    const vitalsList = formData.vitals.map(vital => {
      // Extract blood pressure components
      let systolicBp = null;
      let diastolicBp = null;
      if (vital.bp) {
        const bpParts = vital.bp.split('/');
        systolicBp = bpParts[0] ? parseInt(bpParts[0].trim()) : null;
        diastolicBp = bpParts[1] ? parseInt(bpParts[1].trim()) : null;
      }

      // Format the record time to be SQL Time compatible
      const formattedRecordTime = formatTimeForSql(vital.recordTime);

      return {
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        height: vital.height || null,
        heightUnit: "cm",
        weight: vital.weight || null,
        weightUnit: "kg",
        pulse: vital.pulse || null,
        pulseUnit: "bpm",
        temperature: vital.temperature || null,
        temperatureUnit: "°F",
        systolicBp: systolicBp,
        diastolicBp: diastolicBp,
        bp: vital.bp || null,
        bpUnit: "mmHg",
        spo2: vital.spo2 || null,
        spo2Unit: "%",
        respiratoryRate: vital.respiratoryRate || null,
        respiratoryRateUnit: "BRPM",
        bloodGlucose: vital.bloodGlucose || null,
        bloodGlucoseUnit: "mg/dL",
        glucoseTestType: vital.glucoseTestType || null,
        bsa: vital.bsa || null,
        bmi: vital.bmi || null,
        recordTime: formattedRecordTime,
        sequenceNumber: vital.sequenceNumber || null
      };
    });

    // Process complaints to ensure dates are set
    const processedComplaints = formData.complaints
      .filter(c => c.complaint || c.frequency || c.severity || c.duration)
      .map(c => ({
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        complaint: c.complaint,
        frequency: c.frequency,
        severity: c.severity,
        duration: c.duration,
        complaintDate: c.date || currentDateStr
      }));

    // Process secondary diagnoses to ensure dates are set
    const processedSecondaryDiagnoses = formData.secondaryDiagnosis
      .filter(d => d.diagnosis || d.duration || d.comments)
      .map(d => ({
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        diagnosis: d.diagnosis,
        duration: d.duration,
        comments: d.comments,
        diagnosisDate: d.date || currentDateStr
      }));

    // Process treatments to ensure dates are set
    const processedTreatments = formData.treatment
      .filter(t => t.treatmentType || t.description)
      .map(t => ({
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        treatmentType: t.treatmentType,
        description: t.description,
        treatmentDate: t.date || currentDateStr,
        createdByDTO: { userId: parseInt(doctorId) },
        updatedByDTO: { userId: parseInt(doctorId) }
      }));

    // Process medicines for medicineXrefList - NO separate API calls
    const medicineXrefList = [];

    for (const med of formData.prescription) {
      // Only process filled prescription rows
      if (med.medicine && med.dose && med.when && (med.duration || (med.durationValue && med.durationUnit)) && med.instructions) {
        
        // Find the medicine in medicinesArr by name to get the correct medicineId
        const existingMedicine = medicinesArr.find(m => m.name === med.medicine);
        let medicineId = existingMedicine ? existingMedicine.medicineId : null;

        // If medicine doesn't exist, create it first using the saveMedicine function
        if (!medicineId) {
          try {
            const newMedicine = await saveMedicine(med.medicine);
            if (newMedicine && newMedicine.medicineId) {
              medicineId = newMedicine.medicineId;
            }
          } catch (error) {
            throw new Error(`Failed to create medicine: ${med.medicine}`);
          }
        }

        if (medicineId) {
          // Format duration
          let duration = med.duration;
          if (!duration && (med.durationValue || med.durationUnit)) {
            duration = `${med.durationValue || ''} ${med.durationUnit || ''}`.trim();
          }

          // Add to medicineXrefList - similar to how secondaryDiagnosisList is processed
          medicineXrefList.push({
            medicineId: medicineId,
            patientId: parseInt(patientId),
            medicineName: med.medicine, // Include medicine name as per your payload example
            dose: med.dose,
            when: med.when,
            duration: duration,
            quantity: med.quantity || "",
            unit: med.unit || "",
            instructions: med.instructions,
            activeInd: "Y"
          });
        }
      }
    }

    // Create the payload with correct field mappings and time format
    const payload = {
      patientId: parseInt(patientId),
      visitDate: formData.visitDate,
      patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
      doctorId: parseInt(doctorId),
      createdByDTO: { userId: parseInt(doctorId) },
      updatedByDTO: { userId: parseInt(doctorId) },

      // Use medicineXrefList instead of separate API calls - backend will handle saving medicines
      medicineXrefList: medicineXrefList,

      vitalsList: vitalsList,
      complaintsList: processedComplaints,

      presentIllnessList: formData.historyOfPresentIllness ? [{
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        historyText: formData.historyOfPresentIllness
      }] : [],

      clinicalAssessmentList: [{
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        diseaseStatus: formData.clinicalAssessment.statusOfDisease,
        ecog: formData.clinicalAssessment.ecog || "",
        kps: formData.clinicalAssessment.kps || "",
        examinationNotes: formData.clinicalAssessment.onExamination
      }],

      treatmentList: processedTreatments,
      secondaryDiagnosisList: processedSecondaryDiagnoses,

      adviceList: (formData.adviceNotes || formData.reviewNotes || formData.referTo) ? [{
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        adviceNotes: formData.adviceNotes || "",
        reviewNotes: formData.reviewNotes || "",
        referTo: formData.referTo || "",
        dischargeDate: convertDischargeDateForBackend(formData.dischargeDate),
        dischargeTime: formatTimeForSql(formData.dischargeTime)
      }] : [],

      nextVisitList: formData.nextVisitDate ? [{
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        nextVisitDate: formData.nextVisitDate,
        duration: formData.nextVisitDuration
      }] : [],

      testsRequestedList: formData.testsRequested ? [{
        patientId: parseInt(patientId),
        patientIniAssessmentId: assessmentId ? parseInt(assessmentId) : 0,
        testName: formData.testsRequested
      }] : []
    };

    // Add visitId if updating an existing record
    if (currentEditIndex !== null && formData.visitId) {
      payload.visitId = formData.visitId;
    }

    // Determine the correct endpoint - Use patientOpVisit save/update API only
    const isUpdating = currentEditIndex !== null && formData.visitId;
    const endpoint = isUpdating
      ? `${C.getUrl()}/patientOpVisit/update`
      : `${C.getUrl()}/patientOpVisit/save`;

    // Use correct HTTP method: POST for save, PUT for update
    const httpMethod = isUpdating ? 'PUT' : 'POST';


    // Make the API call - Single API call for everything
    const response = await fetch(endpoint, {
      method: httpMethod,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });


    // Check if response is OK (status 200-299)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText || response.statusText}`);
    }

    // Try to parse JSON response
    let data;
    try {
      const responseText = await response.text();
      
      // Check if response is empty
      if (!responseText) {
        throw new Error("Empty response from server");
      }
      
      data = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Invalid JSON response from server: ${parseError.message}`);
    }

    // Check for success - support multiple possible response formats
    const isSuccess = data.serviceStatus === "SUCCESS" || 
                      data.status === "SUCCESS" || 
                      data.code === "SUCCESS" ||
                      data.statusCode === 200 ||
                      response.status === 200;

    if (isSuccess) {
      
      // Show success message with longer duration
      swal({
        title: "✅ Success!",
        text: currentEditIndex !== null ? "OP Visit updated successfully" : "OP Visit saved successfully",
        icon: "success",
        buttons: {
          confirm: {
            text: "OK",
            value: true,
            visible: true,
            closeModal: true
          }
        },
        timer: 5000,
      });

      // Refresh the OP visits list and hide the form
      await fetchPatientOpVisits();
      setIsFormVisible(false);
      setFormData(initializeFormData());
      setCurrentEditIndex(null);

      // Return success response
      return {
        success: true,
        message: currentEditIndex !== null ? "OP Visit updated successfully" : "OP Visit saved successfully"
      };
    } else {
      const errorMsg = data.message || data.error || "Failed to save OP Visit";
      throw new Error(errorMsg);
    }
  } catch (error) {

    // Only show error message if not called externally
    if (!externalAssessmentId) {
      swal({
        title: "❌ Error!",
        text: error.message || "Failed to save OP Visit. Check browser console for details.",
        icon: "error",
        buttons: {
          confirm: {
            text: "OK",
            value: true,
            visible: true,
            closeModal: true
          }
        },
        timer: 5000,
      });
    }

    // Return error response
    return {
      success: false,
      error: error.message || "Failed to save OP Visit"
    };
  } finally {
    setSaveLoader(false);
  }
};


  const hasUserEnteredData = () => {
    // Check if any vital signs are filled
    const hasVitals = formData.vitals.some(vital =>
      Object.entries(vital).some(([fieldName, value]) =>
        value !== "" && value !== null && value !== undefined &&
        // Don't consider default values as user-entered data
        !(typeof value === 'number' && value === 0) &&
        !(fieldName === 'sequenceNumber' && value === 1) &&  // Don't count default sequence numbers
        !(fieldName === 'recordTime' && value === formData.visitTime) // Don't count default times
      )
    );

    // Check if any complaints are filled with non-empty values
    const hasComplaints = formData.complaints.some(complaint =>
      Object.entries(complaint).some(([fieldName, value]) =>
        fieldName !== "date" && fieldName !== "durationUnit" && // Ignore default fields
        value !== "" && value !== null && value !== undefined
      )
    );

    // Check if history is filled with non-empty text
    const hasHistory = formData.historyOfPresentIllness !== "";

    // Check if clinical assessment is filled with actual data
    const hasClinicalAssessment = Object.entries(formData.clinicalAssessment).some(([fieldName, value]) =>
      value !== "" && value !== null && value !== undefined
    );

    // Check if secondary diagnoses contain actual data
    const hasSecondaryDiagnosis = formData.secondaryDiagnosis.some(diagnosis =>
      Object.entries(diagnosis).some(([fieldName, value]) =>
        fieldName !== "date" && fieldName !== "durationUnit" && // Ignore default fields
        value !== "" && value !== null && value !== undefined
      )
    );

    // Check if prescriptions contain actual data
    const hasPrescription = formData.prescription.some(med =>
      Object.entries(med).some(([fieldName, value]) =>
        fieldName !== "durationUnit" && // Ignore default fields
        value !== "" && value !== null && value !== undefined
      )
    );

    // Check if treatments contain actual data
    const hasTreatment = formData.treatment.some(treatment =>
      Object.entries(treatment).some(([fieldName, value]) =>
        fieldName !== "date" && // Ignore default date
        value !== "" && value !== null && value !== undefined
      )
    );

    // Check if other fields contain actual data
    const hasAdviceNotes = formData.adviceNotes !== "";
    const hasTestsRequested = formData.testsRequested !== "";
    const hasReviewNotes = formData.reviewNotes !== "";
    const hasReferTo = formData.referTo !== "";
    const hasNextVisitDate = formData.nextVisitDate !== "";

    // Return true if any section has significant user-entered data
    return hasVitals || hasComplaints || hasHistory || hasClinicalAssessment ||
      hasSecondaryDiagnosis || hasPrescription || hasTreatment ||
      hasAdviceNotes || hasTestsRequested || hasReviewNotes ||
      hasReferTo || hasNextVisitDate;
  };


  // Function to fetch patient OP visits
  // Update the fetchPatientOpVisits function to ensure empty arrays for tabular sections
  const fetchPatientOpVisits = async () => {
    try {
      // Get patient ID from localStorage
      const patientId = localStorage.getItem("PatientpatientId");

      // Validate patient ID
      if (!patientId || isNaN(parseInt(patientId))) {
        swal({
          title: "Error!",
          text: "Invalid patient ID. Please select a patient first.",
          icon: "error",
          buttons: false,
          timer: 3000,
        });
        return;
      }

      const apiUrl = `${C.getUrl()}/patientOpVisit/get/${patientId}`;

      // Make the API call
      const response = await fetch(apiUrl);
      
      // Check if the response is successful
      if (!response.ok) {
        
        let errorDetails = "";
        try {
          const errorText = await response.text();
          errorDetails = errorText;
        } catch (textError) {
        }
        
        swal({
          title: "Error!",
          text: `Failed to fetch OP visits. Server returned status ${response.status}. Please contact support if the problem persists.`,
          icon: "error",
          buttons: false,
          timer: 3000,
        });
        setOpVisits([]);
        return;
      }
      
      const data = await response.json();
      if (!isMountedRef.current) return;

      // Handle the response based on its format
      let visits = [];

      if (Array.isArray(data)) {
        // Direct array format like in your example
        visits = data;
      } else if (data.statusCode === 200 && data.message) {
        // The format with statusCode and message wrapper
        try {
          visits = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        } catch (parseError) {
          visits = [];
        }
      } else if (data.statusCode === 451) {
        // No records found
        setOpVisits([]);
        return;
      } else {
        setOpVisits([]);
        return;
      }

      // Ensure visits is an array
      if (!Array.isArray(visits)) {
        setOpVisits([]);
        return;
      }


      const formattedVisits = visits.map(visit => {
        // FIX: Correct timezone offset for dates - backend returns dates shifted by -1 day
        // This is a known issue with UTC/local timezone handling
        const correctDateOffset = (dateStr) => {
          if (!dateStr) return dateStr;
          
          // Parse the date string
          const date = new Date(dateStr + 'T00:00:00Z'); // Treat as UTC to avoid browser timezone issues
          
          // Add 1 day to correct the offset issue
          date.setUTCDate(date.getUTCDate() + 1);
          
          // Format back to YYYY-MM-DD
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          
          return `${year}-${month}-${day}`;
        };
        
        // Format vitals - now handle multiple vitals records
        let vitals = [];

        if (visit.vitalsList && visit.vitalsList.length > 0) {
          // Sort vitals by sequence number if present
          const sortedVitals = [...visit.vitalsList].sort((a, b) =>
            (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
          );

          vitals = sortedVitals.map(v => ({
            height: v.height,
            weight: v.weight,
            pulse: v.pulse,
            temperature: v.temperature,
            bp: v.bp || v.bloodPressure,
            spo2: v.spo2,
            respiratoryRate: v.respiratoryRate,
            bloodGlucose: v.bloodGlucose,
            glucoseTestType: v.glucoseTestType,
            bsa: v.bsa,
            bmi: v.bmi,
            recordTime: v.recordTime,
            sequenceNumber: v.sequenceNumber || 1
          }));
        } else {
          // If no vitals list, create an empty array
          vitals = [];
        }

        return {
          id: visit.visitId,
          visitId: visit.visitId,
          patientId: visit.patientId,
          patientIniAssessmentId: visit.patientIniAssessmentId,
          visitDate: correctDateOffset(visit.visitDate),
          visitTime: visit.visitTime,
          doctorId: visit.doctorId,
          createdBy: visit.doctorDTO ?
            `${visit.doctorDTO.firstName || ''} ${visit.doctorDTO.lastName || ''}` :
            (visit.createdByDTO ? `${visit.createdByDTO.firstName || ''} ${visit.createdByDTO.lastName || ''}` : ''),

          // Store vitals as an array
          vitals: vitals.length > 0 ? vitals : [{
            height: "",
            weight: "",
            pulse: "",
            temperature: "",
            bp: "",
            spo2: "",
            respiratoryRate: "",
            bloodGlucose: "",
            glucoseTestType: "",
            bsa: "",
            bmi: "",
            recordTime: "",
            sequenceNumber: 1
          }],

          // Always ensure we have arrays, even if empty
          complaints: visit.complaintsList ? visit.complaintsList.map(c => ({
            complaint: c.complaint,
            frequency: c.frequency,
            severity: c.severity,
            duration: c.duration,
            date: correctDateOffset(c.complaintDate)
          })) : [],

          historyOfPresentIllness: visit.presentIllnessList && visit.presentIllnessList.length > 0
            ? visit.presentIllnessList[0].historyText
            : "",

          clinicalAssessment: {
            statusOfDisease: visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0
              ? visit.clinicalAssessmentList[0].diseaseStatus
              : "",
            ecog: visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0
              ? visit.clinicalAssessmentList[0].ecog
              : "",
            kps: visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0
              ? visit.clinicalAssessmentList[0].kps
              : "",
            onExamination: visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0
              ? visit.clinicalAssessmentList[0].examinationNotes
              : ""
          },

          // Always initialize these as arrays, even if empty
          secondaryDiagnosis: visit.secondaryDiagnosisList ? visit.secondaryDiagnosisList.map(d => ({
            diagnosis: d.diagnosis,
            duration: d.duration,
            comments: d.comments,
            date: correctDateOffset(d.diagnosisDate)
          })) : [],

          prescription: visit.medicineXrefList ? visit.medicineXrefList.map(p => ({
            medicine: p.medicineName,
            dose: p.dose,
            when: p.when,
            duration: p.duration,
            quantity: p.quantity || "",
            unit: p.unit || "",
            instructions: p.instructions,
            visitId: visit.visitId
          })) : [],

          treatment: visit.treatmentList ? visit.treatmentList.map(t => ({
            treatmentType: t.treatmentType,
            description: t.description,
            date: correctDateOffset(t.treatmentDate)
          })) : [],

          adviceNotes: visit.adviceList && visit.adviceList.length > 0
            ? visit.adviceList[0].adviceNotes
            : "",
          reviewNotes: visit.adviceList && visit.adviceList.length > 0
            ? visit.adviceList[0].reviewNotes
            : "",
          referTo: visit.adviceList && visit.adviceList.length > 0
            ? visit.adviceList[0].referTo
            : "",

          nextVisitDate: visit.nextVisitList && visit.nextVisitList.length > 0
            ? correctDateOffset(visit.nextVisitList[0].nextVisitDate)
            : "",
          nextVisitDuration: visit.nextVisitList && visit.nextVisitList.length > 0
            ? visit.nextVisitList[0].duration
            : "1-2 days",

          testsRequested: visit.testsRequestedList && visit.testsRequestedList.length > 0
            ? visit.testsRequestedList[0].testName
            : ""
        };
      });

     // Then update your sorting to be more robust:
formattedVisits.sort((a, b) => {
  // First compare dates
  const dateA = new Date(a.visitDate);
  const dateB = new Date(b.visitDate);
  
  if (dateA.getTime() !== dateB.getTime()) {
    return dateB.getTime() - dateA.getTime(); // Newest date first
  }
  
  // Log times for debugging if needed
  
  // If dates are the same, compare times - ensure we handle missing times
  const timeA = a.visitTime ? convertTimeToMinutes(a.visitTime) : 0;
  const timeB = b.visitTime ? convertTimeToMinutes(b.visitTime) : 0;
  
  
  // If both times are valid and different, sort by time
  if (timeA && timeB && timeA !== timeB) {
    return timeB - timeA; // Latest time first
  }
  
  // If times are same or invalid, fall back to ID sort for stable ordering
  return b.visitId - a.visitId;
});
  
      setOpVisits(formattedVisits);

      // After loading OP visits, fetch patient medicines to supplement prescription data
      fetchPatientMedicines(patientId);

    } catch (error) {
      
      // Determine error type and provide specific message
      let errorMessage = "Failed to fetch OP visits. Please try again.";
      if (error.message && error.message.includes("Failed to fetch")) {
        errorMessage = "Network error: Unable to reach the server. Please check your connection.";
      }
      
      setOpVisits([]);
      swal({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        buttons: false,
        timer: 3000,
      });
    }
  };

  // Fetch OP visits when component mounts
  useEffect(() => {
    fetchPatientOpVisits();
    fetchMedicines();
    fetchComplaints();
    fetchFrequencies();
    fetchSeverities();
    fetchDurations();
    fetchDoses();
    fetchWhens();
    fetchPrescriptionDurations();
    fetchUnits();
    fetchInstructions();
    fetchTreatmentTypes();
    fetchDiseaseStatus();
    fetchDiagnosis();
    fetchTestOptions();
  }, []);

  // Function to fetch medicines
  const fetchMedicines = async () => {
    try {
      setLoadingMedicines(true);

      const res = await fetch(`${C.getUrl()}/medicineListMaster/getAllMedicines`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const medicineArray = Array.isArray(data) ? data : [];

          // Map the fields correctly - ensure we're using the correct field names and preserving the medicineId
          const medicines = medicineArray
            .map(d => ({
              id: d.medicineId,
              name: d.medicineName || d.medicine, // Accept either field name
              medicineId: d.medicineId // Make sure to keep the original medicineId
            }))
            .filter(m => m.name) // Filter out items with no name
            .sort((a, b) => a.name.localeCompare(b.name));

          setMedicinesArr(medicines);
        } catch (parseErr) {
          setMedicinesArr([]);
        }
      } else {

        // Set default medicines as fallback if the API fails
        const defaultMedicines = [
          { id: 1, name: "Paracetamol", medicineId: 1 },
          { id: 2, name: "Ibuprofen", medicineId: 2 },
          { id: 3, name: "Amoxicillin", medicineId: 3 },
          { id: 4, name: "Omeprazole", medicineId: 4 },
          { id: 5, name: "Aspirin", medicineId: 5 }
        ];
        setMedicinesArr(defaultMedicines);
      }
    } catch (err) {

      // Set default medicines as fallback if the API fails
      const defaultMedicines = [
        { id: 1, name: "Paracetamol", medicineId: 1 },
        { id: 2, name: "Ibuprofen", medicineId: 2 },
        { id: 3, name: "Amoxicillin", medicineId: 3 },
        { id: 4, name: "Omeprazole", medicineId: 4 },
        { id: 5, name: "Aspirin", medicineId: 5 }
      ];
      setMedicinesArr(defaultMedicines);
    } finally {
      if (isMountedRef.current) setLoadingMedicines(false);
    }
  };


  // Function to save a new test requested
  const saveTestRequested = async (testName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format
      const payload = {
        testsRequestedName: testName.trim(),
        testsRequestedDescription: `${testName.trim()} description`, // Adding a description
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Show loading indicator before API call
      setTestSearchInput(""); // Clear the search input
      setShowTestOptions(false); // Hide the dropdown during saving

      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveTestsRequestedMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      if (saveResult.statusCode === 200 || saveResult.serviceStatus === "SUCCESS") {
        // Show success message
        swal({
          title: "Success!",
          text: `Test "${testName}" added successfully`,
          icon: "success",
          buttons: false,
          timer: 2000,
        });

        // Add the new test to the selected tests
        setSelectedTests(prev => [...prev, testName.trim()]);

        // Refresh the test options list
        await fetchTestOptions();
      } else {
        throw new Error(saveResult.message || "Failed to save test");
      }
    } catch (err) {

      swal({
        title: "Error!",
        text: `Failed to add test: ${err.message}`,
        icon: "error",
        buttons: false,
        timer: 3000,
      });
    }
  };

  // Function to save a new medicine
  const saveMedicine = async (medicineName) => {

    // const formatDate = (date) => {
    //   const d = new Date(date);
    //   return d.toISOString().split('T')[0];
    // };
    // Helper function to format date for display (DD/MM/YYYY)
const formatDate = (dateString) => {
  if (!dateString) return "";

  // Check if the date is already in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Handle different input formats
  const date = new Date(dateString);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Create payload with the correct structure
      const payload = {
        medicineName: medicineName.trim(),
        activeInd: "Y",
        createdById: parseInt(userId),
        updatedById: parseInt(userId),
        creationDate: formatDate(new Date()),
        updatedDate: formatDate(new Date())
      };


      // Log the exact payload being sent to the API

      // Use the correct API endpoint
      const apiUrl = `${C.getUrl()}/medicineListMaster/saveMedicine`;

      // Call the API
      const saveResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Log the raw response

      // Parse the response
      const responseText = await saveResponse.text();

      let saveResult;
      try {
        saveResult = JSON.parse(responseText);
      } catch (parseError) {
        // If we get a 200 status but can't parse JSON, consider it a success
        if (saveResponse.status === 200) {
          swal({
            title: "Success!",
            text: `Medicine added successfully`,
            icon: "success",
            buttons: false,
            timer: 2000,
          });
          // Refresh the medicine list
          await fetchMedicines();
          return {
            id: saveResult.id,
            name: medicineName,
            medicineId: saveResult.id
          };
        } else {
          throw new Error(`Server returned invalid JSON: ${responseText}`);
        }
      }

      // Consider HTTP 200 as success regardless of response body format
      if (saveResponse.status === 200) {
        swal({
          title: "Success!",
          text: `Medicine added successfully`,
          icon: "success",
          buttons: false,
          timer: 2000,
        });

        // Refresh the medicine list
        await fetchMedicines();

        // Return the newly created medicine
        return {
          id: saveResult.id,
          name: medicineName,
          medicineId: saveResult.id
        };
      } else {
        throw new Error((saveResult && saveResult.message) || "Failed to save medicine");
      }
    } catch (err) {

      swal({
        title: "Error!",
        text: `Failed to add medicine: ${err.message}`,
        icon: "error",
        buttons: false,
        timer: 3000,
      });

      return null;
    }
  };

  // Update the handleSaveMedicinesClick function to properly handle saved prescriptions
  const handleSaveMedicinesClick = async () => {
    try {
      // Filter out non-empty prescription rows 
      const medicinesToSave = formData.prescription.filter(p => 
        (p.medicine && p.dose && p.when && (p.duration || (p.durationValue && p.durationUnit)) && p.instructions) && 
        (p.isModified || !p.medicineListXrefId) // Save if modified or new
      );
  
      if (medicinesToSave.length === 0) {
        swal({
          title: "No Medicines",
          text: "There are no medicines to save",
          icon: "info",
          buttons: false,
          timer: 2000,
        });
        return;
      }
      
      // Get patientId from localStorage
      const patientId = localStorage.getItem("PatientpatientId");
      if (!patientId) {
        swal({
          title: "Error",
          text: "Patient ID not found",
          icon: "error",
          buttons: false,
          timer: 2000,
        });
        return;
      }
      
      // Show loading indicator
      setSaveLoader(true);
      
      const xrefIds = [];
      
      // Save each medicine
      const savePromises = medicinesToSave.map(async (med) => {
        try {
          // Find the medicine in medicinesArr by name to get the correct medicineId
          const existingMedicine = medicinesArr.find(m => m.name === med.medicine);
          const medicineId = existingMedicine ? existingMedicine.medicineId : null;
          
          if (!medicineId) {
            return null;
          }
          
          // Format duration
          let duration = med.duration;
          if (!duration && (med.durationValue || med.durationUnit)) {
            duration = `${med.durationValue || ''} ${med.durationUnit || ''}`.trim();
          }
          
          // Get the current visit ID from the form data if editing an existing visit
          const currentVisitId = currentEditIndex !== null && formData.visitId ? formData.visitId : 0;
          
          const payload = {
            medicineId: medicineId,
            patientId: parseInt(patientId),
            dose: med.dose,
            when: med.when,
            duration: duration,
            quantity: med.quantity || "",
            unit: med.unit || "",
            instructions: med.instructions,
            activeInd: "Y",
            visitId: med.visitId || currentVisitId // Use existing visitId or current visit ID
          };
          
          
          // Determine if this is an update or a new save based on existing medicineListXrefId
          const endpoint = med.medicineListXrefId 
            ? `${C.getUrl()}/medicineListXref/update/${med.medicineListXrefId}`
            : `${C.getUrl()}/medicineListXref/add`;
          
          const method = med.medicineListXrefId ? 'PUT' : 'POST';
          
          const response = await fetch(endpoint, {
            method: method,
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          
          if (!response.ok) {
            throw new Error(`Failed to save medicine: ${med.medicine}`);
          }
          
          const responseData = await response.json();
          
          // If it's a new medicine, extract the medicineListXrefId from the response
          if (!med.medicineListXrefId && responseData.message) {
            try {
              const messageObj = typeof responseData.message === 'string' 
                ? JSON.parse(responseData.message) 
                : responseData.message;
              if (messageObj.medicineListXrefId) {
                xrefIds.push(messageObj.medicineListXrefId);
              }
            } catch (e) {
            }
          } else if (med.medicineListXrefId) {
            // Add existing xrefId to the array
            xrefIds.push(med.medicineListXrefId);
          }
          
          return med.medicine;
        } catch (error) {
          return null;
        }
      });
      
      const results = await Promise.all(savePromises);
      const successful = results.filter(result => result !== null);
      
      if (successful.length > 0) {
        // Update the savedMedicineXrefIds state with the new IDs
        if (xrefIds.length > 0) {
          setSavedMedicineXrefIds(prev => {
            const newSet = new Set([...prev, ...xrefIds]);
            return Array.from(newSet);
          });
        }
        
        swal({
          title: "Success!",
          text: `Saved ${successful.length} medicines to your list`,
          icon: "success",
          buttons: false,
          timer: 2000,
        });
        
        // Refresh medicines
        fetchPatientMedicines(patientId);
        
        fetchPatientOpVisits();
        
        // Mark all medicines as unmodified after successful save
        setFormData(prevData => ({
          ...prevData,
          prescription: prevData.prescription.map(med => ({
            ...med,
            isModified: false
          }))
        }));
      } else {
        swal({
          title: "Error",
          text: "Failed to save medicines",
          icon: "error",
          buttons: false,
          timer: 2000,
        });
      }
    } catch (error) {
      swal({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      setSaveLoader(false);
    }
  };
  // const handleSaveMedicinesClick = async () => {
  //   try {
  //     // Filter out non-empty prescription rows and rows that haven't been previously saved
  //     const medicinesToSave = formData.prescription.filter(p =>
  //       p.medicine && p.dose && p.when && p.duration && p.instructions &&
  //       // Only save rows that are new or modified
  //       (!p.medicineListXrefId || p.isModified)
  //     );

  //     if (medicinesToSave.length === 0) {
  //       swal({
  //         title: "No Medicines",
  //         text: "There are no new or modified medicines to save",
  //         icon: "info",
  //         buttons: false,
  //         timer: 2000,
  //       });
  //       return;
  //     }

  //     // Get patientId from localStorage
  //     const patientId = localStorage.getItem("PatientpatientId");
  //     if (!patientId) {
  //       swal({
  //         title: "Error",
  //         text: "Patient ID not found",
  //         icon: "error",
  //         buttons: false,
  //         timer: 2000,
  //       });
  //       return;
  //     }

  //     // Show loading indicator
  //     setSaveLoader(true);
  //     const xrefIds = [];

  //     // Save each new or modified medicine
  //     const savePromises = medicinesToSave.map(async (med) => {
  //       // Find the medicine in medicinesArr by name to get the correct medicineId
  //       const existingMedicine = medicinesArr.find(m => m.name === med.medicine);
  //       const medicineId = existingMedicine ? existingMedicine.medicineId : null;

  //       if (!medicineId) {
  //         console.error('Medicine not found in the list:', med.medicine);
  //         return null;
  //       }

  //       const payload = {
  //         medicineId: medicineId,
  //         patientId: parseInt(patientId),
  //         dose: med.dose,
  //         when: med.when,
  //         duration: med.duration,
  //         quantity: med.quantity || "",
  //         unit: med.unit || "",
  //         instructions: med.instructions,
  //         activeInd: "Y",
  //         visitId: 12345
  //       };

  //       try {
  //         console.log(`Saving medicine to list with payload:`, payload);

  //         // Determine if this is an update or a new save based on existing medicineListXrefId
  //         const endpoint = med.medicineListXrefId
  //           ? `${C.getUrl()}/medicineListXref/update/${med.medicineListXrefId}`
  //           : `${C.getUrl()}/medicineListXref/add`;

  //         const method = med.medicineListXrefId ? 'PUT' : 'PUT';

  //         const response = await fetch(endpoint, {
  //           method: method,
  //           headers: {
  //             'Content-Type': 'application/json',
  //             'Accept': 'application/json'
  //           },
  //           body: JSON.stringify(payload)
  //         });

  //         if (!response.ok) {
  //           throw new Error(`Failed to save medicine: ${med.medicine}`);
  //         }

  //         const responseJson = await response.json();
  //         const messageStr = responseJson.message;
  //         const messageObj = JSON.parse(messageStr);

  //         xrefIds.push(messageObj.medicineListXrefId);

  //         setSavedMedicineXrefIds(prev => {
  //           const newSet = new Set([...prev, messageObj.medicineListXrefId]);
  //           return Array.from(newSet);
  //         });
          
  //         console.log(savedMedicineXrefIds);
  //         return med.medicine;
  //       } catch (error) {
  //         console.error(`Error saving medicine ${med.medicine}:`, error);
  //         return null;
  //       }
  //     });


  //     const results = await Promise.all(savePromises);
  //     const successful = results.filter(result => result !== null);

  //     if (successful.length > 0) {
  //       swal({
  //         title: "Success!",
  //         text: `Saved ${successful.length} medicines to your list`,
  //         icon: "success",
  //         buttons: false,
  //         timer: 2000,
  //       });

  //       // Refresh medicines
  //       fetchPatientMedicines(patientId);

  //       fetchPatientOpVisits();

  //       // Reset the modification flag for saved medicines
  //       setFormData(prevData => ({
  //         ...prevData,
  //         prescription: prevData.prescription.map(med => ({
  //           ...med,
  //           isModified: false
  //         }))
  //       }));
  //     } else {
  //       swal({
  //         title: "Error",
  //         text: "Failed to save medicines",
  //         icon: "error",
  //         buttons: false,
  //         timer: 2000,
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error saving medicines:", error);
  //     swal({
  //       title: "Error",
  //       text: "An unexpected error occurred",
  //       icon: "error",
  //       buttons: false,
  //       timer: 2000,
  //     });
  //   } finally {
  //     setSaveLoader(false);
  //   }
  // };


  // Function to fetch complaints
  const fetchComplaints = async () => {
    try {
      setLoadingComplaints(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllComplaintMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const complaintsArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the DTO structure
          const complaints = complaintsArray
            .map(d => ({
              id: d.complaintMasterId,
              name: d.complaintName,
              complaintId: d.complaintMasterId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setComplaintsArr(complaints);
        } catch (parseErr) {
          setComplaintsArr([]);
        }
      } else {
        setComplaintsArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching complaints: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingComplaints(false);
    }
  };

  // Function to save a new complaint
  const saveComplaint = async (complaintName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        complaintName: complaintName,
        complaintDescription: `${complaintName} description`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveComplaintMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchComplaints();

      // Find the newly created complaint in the updated list
      const newComplaint = complaintsArr.find(c => c.name === complaintName);
      if (newComplaint) {
        return newComplaint;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: complaintName,
        complaintId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch frequencies
  const fetchFrequencies = async () => {
    try {
      setLoadingFrequencies(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllFrequencyMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const frequenciesArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the DTO structure
          const frequencies = frequenciesArray
            .map(d => ({
              id: d.frequencyMasterId,
              name: d.frequencyName,
              frequencyId: d.frequencyMasterId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setFrequenciesArr(frequencies);
        } catch (parseErr) {
          setFrequenciesArr([]);
        }
      } else {
        setFrequenciesArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching frequencies: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingFrequencies(false);
    }
  };

  // Function to save a new frequency
  const saveFrequency = async (frequencyName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        frequencyName: frequencyName,
        frequencyDescription: `${frequencyName} description`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveFrequencyMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchFrequencies();

      // Find the newly created frequency in the updated list
      const newFrequency = frequenciesArr.find(f => f.name === frequencyName);
      if (newFrequency) {
        return newFrequency;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: frequencyName,
        frequencyId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch severities
  const fetchSeverities = async () => {
    try {
      setLoadingSeverities(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllSeverityMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const severitiesArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the DTO structure
          const severities = severitiesArray
            .map(d => ({
              id: d.severityMasterId,
              name: d.severityName,
              severityId: d.severityMasterId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setSeveritiesArr(severities);
        } catch (parseErr) {
          setSeveritiesArr([]);
        }
      } else {
        setSeveritiesArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching severities: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingSeverities(false);
    }
  };

  // Function to save a new severity
  const saveSeverity = async (severityName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        severityName: severityName,
        severityDescription: `${severityName} description`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveSeverityMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchSeverities();

      // Find the newly created severity in the updated list
      const newSeverity = severitiesArr.find(s => s.name === severityName);
      if (newSeverity) {
        return newSeverity;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: severityName,
        severityId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  const handleSelectChange1 = (selectedOption) => {

    // Handle both object format and direct value format
    const kpsValue = selectedOption && selectedOption.value !== undefined
      ? selectedOption.value
      : (selectedOption || '');


    setFormData(prevData => ({
      ...prevData,
      clinicalAssessment: {
        ...prevData.clinicalAssessment,
        kps: kpsValue
      }
    }));
  };



  // Find the current selected option based on formData
  const selectedOption1 = kpsOptions.find(
    option => option.value === formData.clinicalAssessment.kps
  );

  const handleSelectChange = (selectedOption) => {

    // Handle both object format and direct value format
    const ecogValue = selectedOption && selectedOption.value !== undefined
      ? selectedOption.value
      : (selectedOption || '');


    setFormData(prevData => ({
      ...prevData,
      clinicalAssessment: {
        ...prevData.clinicalAssessment,
        ecog: ecogValue
      }
    }));
  };

  // Find the current selected option based on formData
  const selectedOption = ecogOptions.find(
    option => option.value === formData.clinicalAssessment.ecog
  );

  // Function to fetch durations
  const fetchDurations = async () => {
    try {
      setLoadingDurations(true);

      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllDurationMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const durationsArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the DTO structure
          const durations = durationsArray
            .map(d => ({
              id: d.durationMasterId,
              name: d.durationName,
              durationId: d.durationMasterId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setDurationsArr(durations);

          // Also set for prescription durations since they use the same data
          setPrescriptionDurationsArr(durations);
        } catch (parseErr) {
          setDurationsArr([]);
          setPrescriptionDurationsArr([]);
        }
      } else {
        setDurationsArr([]);
        setPrescriptionDurationsArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching durations: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) {
        setLoadingDurations(false);
        setLoadingPrescriptionDurations(false);
      }
    }
  };

  // Function to save a new duration
  const saveDuration = async (durationName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        durationName: durationName,
        durationDescription: `${durationName} duration`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveDurationMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchDurations();

      // Find the newly created duration in the updated list
      const newDuration = durationsArr.find(d => d.name === durationName);
      if (newDuration) {
        return newDuration;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: durationName,
        durationId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch doses
  const fetchDoses = async () => {
    try {
      setLoadingDoses(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllDoseMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const dosesArray = Array.isArray(data) ? data : [];

          // Sort doses alphabetically
          const doses = dosesArray
            .map(d => ({
              id: d.doseId,
              name: d.doseName,
              doseId: d.doseId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setDosesArr(doses);
        } catch (parseErr) {
          setDosesArr([]);
        }
      } else {
        setDosesArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching doses: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingDoses(false);
    }
  };
  // Function to save a new dose
  const saveDose = async (doseName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload with the correct structure
      const payload = {
        doseName: doseName,
        doseDescription: `${doseName} description`,
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveDoseMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Refresh the dose list
      await fetchDoses();

      // Find the newly created dose in the updated list
      const newDose = dosesArr.find(d => d.name === doseName);
      if (newDose) {
        return newDose;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: doseName,
        doseId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch whens
  const fetchWhens = async () => {
    try {
      setLoadingWhens(true);

      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllWhenMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);
          const whensArray = Array.isArray(data) ? data : [];

          // Sort whens alphabetically
          const whens = whensArray
            .map(d => ({
              id: d.whenId,
              name: d.whenName,
              whenId: d.whenId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setWhensArr(whens);
        } catch (parseErr) {
          setWhensArr([]);
        }
      } else {
        setWhensArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching whens: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingWhens(false);
    }
  };

  // Function to save a new when
  const saveWhen = async (whenName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload with the correct structure
      const payload = {
        whenName: whenName,
        whenDescription: `${whenName} description`,
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveWhenMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Refresh the when list
      await fetchWhens();

      // Find the newly created when in the updated list
      const newWhen = whensArr.find(w => w.name === whenName);
      if (newWhen) {
        return newWhen;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: whenName,
        whenId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch prescription durations
  const fetchPrescriptionDurations = async () => {
    try {
      setLoadingPrescriptionDurations(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllDurationMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      try {
        let data = JSON.parse(resObj.message);
        const durationsArray = Array.isArray(data) ? data : [];

        // Sort durations alphabetically
        const durations = durationsArray
          .map(d => ({
            id: d.durationId,
            name: d.durationName,
            durationId: d.durationId
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setPrescriptionDurationsArr(durations);
      } catch (parseErr) {
        setPrescriptionDurationsArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching prescription durations: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingPrescriptionDurations(false);
    }
  };

  // Function to fetch units
  const fetchUnits = async () => {
    try {
      setLoadingUnits(true);
      const res = await fetch(`${C.getUrl()}/patientOpVisit/unit/getAll`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      try {
        // Check if message is JSON before parsing
        let data = resObj.message;
        if (typeof resObj.message === 'string' && (resObj.message.startsWith('{') || resObj.message.startsWith('['))) {
          data = JSON.parse(resObj.message);
        }
        const unitsArray = Array.isArray(data) ? data : [];

        // Sort units alphabetically
        const units = unitsArray
          .map(d => ({
            id: d.unitId,
            name: d.unitName,
            unitId: d.unitId
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setUnitsArr(units);
      } catch (parseErr) {
        setUnitsArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching units: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingUnits(false);
    }
  };

  // Function to save a new unit
  const saveUnit = async (unitName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload with the correct structure
      const payload = {
        unitName: unitName,
        unitDescription: `${unitName} description`,
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/unit/save`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Refresh the unit list
      await fetchUnits();

      // Find the newly created unit in the updated list
      const newUnit = unitsArr.find(u => u.name === unitName);
      if (newUnit) {
        return newUnit;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: unitName,
        unitId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch instructions
  const fetchInstructions = async () => {
    try {
      setLoadingInstructions(true);
      const res = await fetch(`${C.getUrl()}/opVisitMaster/instruction/getAll`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      try {
        // Check if message is JSON before parsing
        let data = resObj.message;
        if (typeof resObj.message === 'string' && (resObj.message.startsWith('{') || resObj.message.startsWith('['))) {
          data = JSON.parse(resObj.message);
        }
        const instructionsArray = Array.isArray(data) ? data : [];

        // Sort instructions alphabetically
        const instructions = instructionsArray
          .map(d => ({
            id: d.instructionId,
            name: d.instructionName,
            instructionId: d.instructionId
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setInstructionsArr(instructions);
      } catch (parseErr) {
        setInstructionsArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching instructions: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingInstructions(false);
    }
  };

  // Function to save a new instruction
  const saveInstruction = async (instructionName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload with the correct structure
      const payload = {
        instructionName: instructionName,
        instructionDescription: `${instructionName} description`,
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/instruction/save`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Refresh the instruction list
      await fetchInstructions();

      // Find the newly created instruction in the updated list
      const newInstruction = instructionsArr.find(i => i.name === instructionName);
      if (newInstruction) {
        return newInstruction;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: instructionName,
        instructionId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch treatment types
  const fetchTreatmentTypes = async () => {
    try {
      setLoadingTreatmentTypes(true);

      const res = await fetch(`${C.getUrl()}/patientOpVisit/getAllTreatmentTypeMaster`);
      const resObj = await res.json();
      if (!isMountedRef.current) return;

      if (resObj && resObj.message) {
        try {
          let data = JSON.parse(resObj.message);

          const treatmentTypesArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the actual API response structure
          // Using typeId and typeName fields from the response
          const treatmentTypes = treatmentTypesArray
            .map(d => ({
              id: d.typeId,
              name: d.typeName,
              treatmentTypeId: d.typeId
            }))
            .filter(item => item.name && item.name.trim() !== "")
            .sort((a, b) => a.name.localeCompare(b.name));


          setTreatmentTypesArr(treatmentTypes);
        } catch (parseErr) {
          setTreatmentTypesArr([]);
        }
      } else {
        setTreatmentTypesArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching treatment types: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingTreatmentTypes(false);
    }
  };

  // Function to save a new treatment type
  const saveTreatmentType = async (treatmentTypeName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        typeName: treatmentTypeName,
        typeDescription: `${treatmentTypeName} description`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveTreatmentTypeMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchTreatmentTypes();

      // Find the newly created treatment type in the updated list
      const newTreatmentType = treatmentTypesArr.find(t => t.name === treatmentTypeName);
      if (newTreatmentType) {
        return newTreatmentType;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: treatmentTypeName,
        treatmentTypeId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };

  // Function to fetch disease status options
  const fetchDiseaseStatus = async () => {
    try {
      setLoadingDiseaseStatus(true);

      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllDiseaseStatusMaster`);
      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result && result.message) {
        try {
          const data = JSON.parse(result.message);

          const statusArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the actual API response structure
          // Using statusId and statusName fields from the response
          const formattedStatus = statusArray
            .map(status => ({
              id: status.statusId,
              name: status.statusName,
              diseaseStatusId: status.statusId
            }))
            .filter(item => item.name && item.name.trim() !== "")
            .sort((a, b) => a.name.localeCompare(b.name));


          setDiseaseStatusArr(formattedStatus);
        } catch (parseErr) {
          setDiseaseStatusArr([]);
        }
      } else {
        setDiseaseStatusArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching disease status: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingDiseaseStatus(false);
    }
  };


  // Function to save a new disease status
  const saveDiseaseStatus = async (statusName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        statusName: statusName,
        statusDescription: `${statusName} status description`, // Adding a description as shown in Postman
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveDiseaseStatusMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the list
      await fetchDiseaseStatus();

      // Find the newly created status in the updated list
      const newStatus = diseaseStatusArr.find(s => s.name === statusName);
      if (newStatus) {
        return newStatus;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(),
        name: statusName,
        diseaseStatusId: Date.now()
      };
    } catch (err) {
      return null;
    }
  };


  // Function to fetch diagnosis options
  const fetchDiagnosis = async () => {
    try {
      setLoadingDiagnosis(true);

      // Corrected URL path
      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllDiagnosisMaster`);
      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result && result.message) {
        try {
          const data = JSON.parse(result.message);
          const diagnosisArray = Array.isArray(data) ? data : [];

          // Map the fields correctly based on the DTO structure
          const formattedDiagnosis = diagnosisArray
            .map(diagnosis => ({
              id: diagnosis.diagnosisMasterId,
              name: diagnosis.diagnosisName,
              diagnosisId: diagnosis.diagnosisMasterId
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setDiagnosisArr(formattedDiagnosis);
        } catch (parseErr) {
          setDiagnosisArr([]);
        }
      } else {
        setDiagnosisArr([]);
      }
    } catch (err) {
      swal({
        title: "Error!",
        text: "Error fetching diagnosis: " + err.message,
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    } finally {
      if (isMountedRef.current) setLoadingDiagnosis(false);
    }
  };

  // Function to save a new diagnosis
  const saveDiagnosis = async (diagnosisName) => {
    try {
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || "1";

      // Prepare the payload according to the correct format from Postman
      const payload = {
        diagnosisName: diagnosisName,
        diagnosisDescription: `${diagnosisName} description`, // Adding a description as shown in your Postman example
        activeInd: "Y",
        createdByDTO: { userId: parseInt(userId) },
        updatedByDTO: { userId: parseInt(userId) }
      };


      // Call the API
      const saveResponse = await fetch(`${C.getUrl()}/patientOpVisit/saveDiagnosisMaster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveResult = await saveResponse.json();

      // Regardless of API response, refresh the diagnosis list
      await fetchDiagnosis();

      // Find the newly created diagnosis in the updated list
      const newDiagnosis = diagnosisArr.find(d => d.name === diagnosisName);
      if (newDiagnosis) {
        return newDiagnosis;
      }

      // If it wasn't found, create a temporary one to use in UI
      return {
        id: Date.now(), // Temporary ID
        name: diagnosisName,
        diagnosisId: Date.now() // Temporary ID
      };
    } catch (err) {
      return null;
    }
  };

  // Print current OP Visit
  // Helper function to validate print data before printing (CRITICAL FIX #2)
  const validatePrintData = (visitData) => {
    const warnings = [];
    const errors = [];

    if (!visitData) {
      errors.push("Visit data not found");
      return { errors, warnings };
    }

    // CRITICAL ERRORS - Block printing
    // ===================================

    // Check if vitals exist
    if (!visitData.vitals || visitData.vitals.length === 0) {
      errors.push("ERROR: No vital signs recorded. Vitals (Height, Weight) are mandatory.");
    }

    // Check vital data completeness
    const firstVital = visitData.vitals?.[0];
    if (firstVital && (!firstVital.height || !firstVital.weight)) {
      errors.push("ERROR: Height and Weight are required in vital signs.");
    }

    // Check medicine fetch status
    if (medicinesFetchStatus === 'error') {
      errors.push(`ERROR: Medicine fetch failed - ${medicinesFetchError}. Cannot generate complete prescription list.`);
    } else if (medicinesFetchStatus === 'loading') {
      errors.push("ERROR: Medicine data is still loading. Please wait for completion before printing...");
    }

    // Check if medicines are available from BOTH sources (prescription + xref)
    const prescriptionMedicines = visitData.prescription && visitData.prescription.filter(med => med.medicine).length > 0;
    const xrefMedicines = getMedicinesForVisit(visitData.visitId) || [];
    const hasXrefMedicines = xrefMedicines.length > 0;
    
    // Only warn if no medicines from EITHER source
    if (!prescriptionMedicines && !hasXrefMedicines) {
      warnings.push("⚠️  WARNING: No medicines prescribed for this visit");
    }

    // WARNINGS - Non-blocking but user should review
    // =============================================

    // Check if clinical assessment is complete
    const hasStatusOfDisease = visitData.clinicalAssessment?.statusOfDisease;
    const hasEcog = visitData.clinicalAssessment?.ecog;
    const hasKps = visitData.clinicalAssessment?.kps;
    const hasExamination = visitData.clinicalAssessment?.onExamination;
    
    if (!hasStatusOfDisease || !hasEcog || !hasKps || !hasExamination) {
      warnings.push("⚠️  WARNING: Clinical assessment is incomplete (Missing: " + 
        [!hasStatusOfDisease && "Disease Status", !hasEcog && "ECOG", !hasKps && "KPS", !hasExamination && "Examination"]
          .filter(Boolean).join(", ") + ")");
    }

    // Check if complaints are recorded
    if (!visitData.complaints || visitData.complaints.length === 0) {
      warnings.push("⚠️  WARNING: No chief complaints recorded");
    }

    // Check if secondary diagnosis is recorded
    const hasSecondaryDiagnosis = visitData.secondaryDiagnosis && 
      visitData.secondaryDiagnosis.some(d => d.diagnosis || d.duration || d.comments);
    if (!hasSecondaryDiagnosis) {
      warnings.push("⚠️  WARNING: No secondary diagnosis recorded");
    }

    // Check if treatment plan is documented
    const hasTreatment = visitData.treatment && visitData.treatment.length > 0;
    if (!hasTreatment) {
      warnings.push("⚠️  WARNING: No treatment plan documented");
    }

    // Check if tests are requested
    const hasTestsRequested = visitData.testsRequested && visitData.testsRequested.trim() !== "";
    if (!hasTestsRequested) {
      warnings.push("⚠️  WARNING: No diagnostic tests requested");
    }

    // Check history of present illness
    const hasHistory = visitData.historyOfPresentIllness && visitData.historyOfPresentIllness.trim() !== "";
    if (!hasHistory) {
      warnings.push("⚠️  WARNING: History of present illness not documented");
    }

    // Calculate data completeness score
    const completenessChecks = [
      visitData.vitals?.length > 0 && visitData.vitals[0]?.height && visitData.vitals[0]?.weight,
      prescriptionMedicines || hasXrefMedicines,
      hasStatusOfDisease && hasEcog && hasKps && hasExamination,
      visitData.complaints?.length > 0,
      hasSecondaryDiagnosis,
      hasTreatment,
      hasTestsRequested,
      hasHistory
    ];
    
    const completenessScore = Math.round((completenessChecks.filter(Boolean).length / completenessChecks.length) * 100);
    
    // Add completeness score to errors for display
    if (completenessScore < 50) {
      errors.push(`WARNING: Patient record is only ${completenessScore}% complete. Consider reviewing all sections.`);
    }

    return { errors, warnings, completenessScore };
  };

  // Helper function to deduplicate medicines from multiple sources
  const deduplicateMedicines = (prescriptionMeds, xrefMeds) => {
    if (!prescriptionMeds) prescriptionMeds = [];
    if (!xrefMeds) xrefMeds = [];

    // Create a map to track unique medicines
    const medicineMap = new Map();

    // Add prescription medicines first (primary source)
    prescriptionMeds.forEach(med => {
      if (med.medicine) {
        const key = `${med.medicine}|${med.dose}|${med.when}`;
        medicineMap.set(key, med);
      }
    });

    // Add xref medicines only if not already present
    xrefMeds.forEach(med => {
      if (med.medicine) {
        const key = `${med.medicine}|${med.dose}|${med.when}`;
        if (!medicineMap.has(key)) {
          medicineMap.set(key, med);
        }
      }
    });

    // Return deduplicated array
    return Array.from(medicineMap.values());
  };

  const printOPVisit = (visitId) => {
    const visitToPrint = opVisits.find(visit => visit.id === visitId || visit.visitId === visitId);
    
    if (!visitToPrint) {
      swal({
        title: "Error!",
        text: "Visit not found",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // TEMPORARY: Completeness validation disabled - print proceeds regardless of completion %
    performPrint(visitToPrint);
  };

  // Helper function to perform the actual print operation
  const performPrint = (visitToPrint) => {
    if (!visitToPrint) return;
    

    // Get xref medicines for this visit as a fallback
    const xrefMedicines = getMedicinesForVisit(visitToPrint.visitId) || [];

    // Deduplicate medicines from both sources
    const deduplicatedMedicines = deduplicateMedicines(visitToPrint.prescription, xrefMedicines);

    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}${hospitalDetails.logo}`;

    // Generate HTML content for printing
    const printContent = `
      <html>
        <head>
          <title>OP Visit - ${formatDate(visitToPrint.visitDate)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            @page { size: auto; margin: 0mm 0mm 12mm 0mm; }
            @page { @bottom-center { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 11px; color: #555; } }
            @page { @bottom-left { content: none; } @bottom-right { content: none; } @top-left { content: none; } @top-center { content: none; } @top-right { content: none; } }

            html, body { width: 100%; margin: 0; padding: 0; }

            body {
              font-family: Arial, sans-serif;
              margin: 15mm 20mm;
              padding: 0;
              line-height: 1.4;
              color: #333;
              width: auto;
              overflow: visible;
            }

            .letterhead {
              display: flex;
              align-items: center;
              padding: 12px 0;
              border-bottom: 2px solid #0056b3;
              margin-bottom: 18px;
            }
            .letterhead-logo { flex: 0 0 90px; display: flex; align-items: center; justify-content: center; }
            .letterhead-logo img { max-width: 80px; max-height: 80px; }
            .letterhead-info { flex: 1; padding-left: 15px; }
            .letterhead-name { font-weight: bold; font-size: 18px; color: #0056b3; margin-bottom: 3px; }
            .letterhead-subname { font-size: 13px; margin-bottom: 4px; }
            .letterhead-address, .letterhead-contact { font-size: 12px; margin-bottom: 2px; }

            .report-title {
              text-align: center;
              font-size: 16px;
              font-weight: bold;
              color: #0056b3;
              margin-bottom: 4px;
            }
            .report-date {
              text-align: center;
              font-size: 13px;
              margin-bottom: 18px;
              color: #555;
            }
            
            .section {
              margin-bottom: 20px;
              margin-left: 0;
              margin-right: 0;
              page-break-inside: avoid;
            }
            
            .section-title {
              font-weight: bold;
              color: #2c3e50;
              border-bottom: 2px solid #2c3e50;
              padding: 8px 0;
              margin: 10px 0;
              font-size: 14px;
              width: 100%;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              border: 2px solid #333;
              table-layout: fixed;
              word-wrap: break-word;
            }
            
            th {
              background-color: #e8e8e8;
              border: 1px solid #333;
              padding: 10px;
              text-align: left;
              font-weight: bold;
              font-size: 12px;
              word-wrap: break-word;
            }
            
            td {
              border: 1px solid #333;
              padding: 8px 10px;
              text-align: left;
              font-size: 12px;
              word-wrap: break-word;
              overflow-wrap: break-word;
              word-break: normal;
              hyphens: manual;
            }
            
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            
            p {
              margin: 8px 0;
              font-size: 12px;
              line-height: 1.5;
            }
            
            h3 {
              font-size: 14px;
              margin: 10px 0 5px 0;
            }
            
            h4 {
              font-size: 13px;
              margin: 8px 0;
            }
          </style>
        </head>
        <body>
          <div class="letterhead">
            <div class="letterhead-logo">
              <img src="${logoUrl}" alt="Hospital Logo" />
            </div>
            <div class="letterhead-info">
              <div class="letterhead-name">${hospitalDetails.name}</div>
              <div class="letterhead-subname">${hospitalDetails.subName}</div>
              <div class="letterhead-address">${hospitalDetails.address}</div>
              <div class="letterhead-contact">
                ${hospitalDetails.phone1}${hospitalDetails.phone2 ? ' | ' + hospitalDetails.phone2 : ''}
                ${hospitalDetails.website ? ' | ' + hospitalDetails.website : ''}
                ${hospitalDetails.email ? ' | ' + hospitalDetails.email : ''}
              </div>
            </div>
          </div>
          <div class="report-title">OP Visit Report</div>
          <div class="report-date">Date: ${formatDate(visitToPrint.visitDate)}</div>
          
          <div class="section">
            <h3 class="section-title">Vitals - Part 1</h3>
            <table>
              <tr>
                <th>Height</th>
                <th>Weight</th>
                <th>Pulse</th>
                <th>BP</th>
                <th>SpO2</th>
                <th>Temperature</th>
                <th>Respiratory Rate</th>
              </tr>
              ${visitToPrint.vitals.map(vital => `
                 <tr>
                 <td>${vital.height ? `${vital.height} cm` : '-'}</td>
                  <td>${vital.weight ? `${vital.weight} kg` : '-'}</td>
                  <td>${vital.pulse ? `${vital.pulse} bpm` : '-'}</td>
                  <td>${vital.bp ? `${vital.bp} mmHg` : '-'}</td>
                  <td>${vital.spo2 ? `${vital.spo2} %` : '-'}</td>
                  <td>${vital.temperature ? `${vital.temperature} °F` : '-'}</td>
                  <td>${vital.respiratoryRate ? `${vital.respiratoryRate} BRPM` : '-'}</td>
              </tr>
                `).join('')}
            </table>
          </div>
          
          <div class="section">
            <h3 class="section-title">Vitals - Part 2</h3>
            <table>
              <tr>
                <th>Blood Glucose</th>
                <th>Glucose Test Type</th>
                <th>BSA</th>
                <th>BMI</th>
                <th>Record Time</th>
              </tr>
              ${visitToPrint.vitals.map(vital => `
                 <tr>
                  <td>${vital.bloodGlucose ? `${vital.bloodGlucose} mg/dL` : '-'}</td>
                  <td>${vital.glucoseTestType || '-'}</td>
                  <td>${vital.bsa ? `${vital.bsa} m²` : '-'}</td>
                  <td>${vital.bmi ? `${vital.bmi} kg/m²` : '-'}</td>
                  <td>${vital.recordTime || '-'}</td>
              </tr>
                `).join('')}
            </table>
          </div>
             ${visitToPrint.complaints && visitToPrint.complaints.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Chief Complaints/Symptoms</h3>
            <table>
              <tr>
                <th>Complaint</th>
                <th>Frequency</th>
                <th>Severity</th>
                <th>Duration</th>
                <th>Date</th>
              </tr>
              ${visitToPrint.complaints.map(cmp => `
                <tr>
                <td>${cmp.complaint ? `${cmp.complaint}` : '-'}</td>
                <td>${cmp.frequency ? `${cmp.frequency}` : '-'}</td>
                <td>${cmp.severity ? `${cmp.severity}` : '-'}</td>
                <td>${cmp.duration ? `${cmp.duration} days` : '-'}</td>
                <td>${cmp.date || '-'}</td>
              </tr>
                `).join('')}
              
            </table>
          </div>` : ''}
          
          ${visitToPrint.historyOfPresentIllness && visitToPrint.historyOfPresentIllness.length > 0 ? `
          <div class="section">
            <h3 class="section-title">History of Present Illness</h3>
            <p>${visitToPrint.historyOfPresentIllness.replace(/\n/g, '<br>')}</p>
          </div>` : ''}
          
          ${visitToPrint.clinicalAssessment && visitToPrint.clinicalAssessment.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Clinical Assessment</h3>
            <p><strong>Status of Disease:</strong> ${visitToPrint.clinicalAssessment.statusOfDisease || 'N/A'}</p>
            <p><strong>ECOG:</strong> ${visitToPrint.clinicalAssessment.ecog || 'N/A'}</p>
            <p><strong>KPS:</strong> ${visitToPrint.clinicalAssessment.kps || 'N/A'}</p>
            <p><strong>On Examination:</strong> ${visitToPrint.clinicalAssessment.onExamination || 'N/A'}</p>
          </div>` : ''}


          ${visitToPrint.secondaryDiagnosis && visitToPrint.secondaryDiagnosis.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Secondary Diagnosis</h3>
            <table>
              <tr>
                <th>Diagnosis</th>
                <th>Duration</th>
                <th>Comments</th>
                <th>Date</th>
              </tr>
              ${visitToPrint.secondaryDiagnosis.map(diagnosis => `
                <tr>
              <td>${diagnosis.diagnosis || '-'}</td>
              <td>${diagnosis.duration ? `${diagnosis.duration}` : '-'}</td>
              <td>${diagnosis.comments || '-'}</td>
              <td>${diagnosis.date || '-'}</td>

              </tr>
                `).join('')}
              
            </table>
          </div>` : ''}


          ${(deduplicatedMedicines && deduplicatedMedicines.length > 0) ? `
          <div class="section">
            <h3 class="section-title">Doctor Prescription</h3>
            <table>
              <tr>
                <th>Medicine</th>
                <th>Dose</th>
                <th>When</th>
                <th>Duration</th>
                <th>Quantity</th>
                <th>Instructions</th>
              </tr>
              ${deduplicatedMedicines.map(med => `
                <tr>
                  <td>${med.medicine || '-'}</td>
                  <td>${med.dose || '-'}</td>
                  <td>${med.when || '-'}</td>
                  <td>${med.duration ? `${med.duration}` : '-'}</td>
                  <td>${med.quantity ? `${med.quantity} ${med.unit || ''}` : '-'}</td>
                  <td>${med.instructions || '-'}</td>

                </tr>
              `).join('')}
            </table>
          </div>` : ''}


          ${visitToPrint.treatment && visitToPrint.treatment.length > 0 ? `
           <div class="section">
            <h3 class="section-title">Treatment</h3>
            <table>
              <tr>
                <th>Treatment</th>
                <th>Description</th>
                <th>Date</th>
              </tr>
              ${visitToPrint.treatment.map(tre => `
                <tr>
                <td>${tre.treatmentType || '-'}</td>
                <td>${tre.description || '-'} </td>
                <td>${tre.date || '-'}</td>
              </tr>`
    ).join('')}
             
            </table>
          </div>` : ''}


          ${visitToPrint.testsRequested && visitToPrint.testsRequested.length > 0 ? `
           <div class="section">
            <h3 class="section-title">Test(s) Prescribed</h3>
            <p>${visitToPrint.testsRequested.replace(/\n/g, '<br>')}</p>
          </div>` : ''}
          
          ${visitToPrint.adviceNotes && visitToPrint.adviceNotes.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Advice/Notes</h3>
            <p>${visitToPrint.adviceNotes.replace(/\n/g, '<br>')}</p>
          </div>` : ''}
          
           ${visitToPrint.reviewNotes && visitToPrint.reviewNotes.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Review Notes</h3>
            <p>${visitToPrint.reviewNotes.replace(/\n/g, '<br>')}</p>
          </div>` : ''}

           ${visitToPrint.referTo && visitToPrint.referTo.length > 0 ? `
           <div class="section">
            <h3 class="section-title">Refer To</h3>
            <p>${visitToPrint.referTo}</p>
          </div>` : ''}
          
          <div class="section">
            <h3 class="section-title">Next Visit Date</h3>
            <p>Date:${visitToPrint.nextVisitDate ? formatDate(visitToPrint.nextVisitDate) : 'Not scheduled'}</p>
          </div>

           <div class="section">
            <h4>Duration:${visitToPrint.nextVisitDuration ? visitToPrint.nextVisitDuration : 'Not scheduled'}</h4>
          </div>
        </body>
      </html>
    `;


    // Create an invisible iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    // Write content to the iframe
    iframe.contentDocument.open();
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();

    // Trigger the print dialog
    iframe.onload = function () {
      iframe.contentWindow.focus(); // Focus on the iframe content
      iframe.contentWindow.print(); // Trigger the print dialog
      document.body.removeChild(iframe); // Clean up the iframe after printing
    };
  };

  // Create appointment based on next visit date
  const createAppointment = () => {
    alert("Appointment would be created here");
  };

  return (
    <div className="diagnosis-container">

      <div className="mb-3 d-flex justify-content-between align-items-center">
        {/* Add button */}
        <div className="mb-3">
          <Button
            className="btn btn-secondary"
            onClick={() => showOPVisitForm()}
          >
            Add
          </Button>
        </div>

        {isFormVisible && (
          <div className="row">
            <div className="col-12 text-end">
              <Button variant="danger" onClick={hideOPVisitForm}>
                Cancel
              </Button>

            </div>
          </div>
        )}
      </div>

      {/* OP Visit form */}
      {isFormVisible && (
        <div className="mb-4 border p-3 rounded">
          <form>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Visit Date <span className="text-danger">*</span></label>
                  {/* <input
                    type="date"
                    className={`form-control ${errors.visitDate ? "is-invalid" : ""}`}
                    name="visitDate"
                    value={formData.visitDate}
                    onChange={handleInputChange}
                  /> */}
 <DatePicker
  format={dateFormat}
  value={formData.visitDate ? moment(formData.visitDate, dateFormat1) : null}
  onChange={(date, dateString) => handleDateChange(date, dateString, "visitDate")}
  className={errors.visitDate ? "is-invalid" : ""}
  style={{ width: '100%' }}
/>
                  {errors.visitDate && (
                    <div className="invalid-feedback">{errors.visitDate}</div>
                  )}
                </div>
              </div>
              {/* <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Visit Time</label>
                  <input
                    type="text"
                    className="form-control"
                    name="visitTime"
                    value={formData.visitTime}
                    onChange={handleInputChange}
                  />
                </div>
              </div> */}
              {/* <div className="col-md-8 text-end">
                <Button variant="danger" onClick={hideOPVisitForm}>
                  Cancel
                </Button>
                <Button
                  className="btn btn-secondary mr-2"
                  onClick={saveOPVisit}
                  disabled={saveLoader}
                >
                  {saveLoader ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div> */}
            </div>

            {/* Vitals Section */}
            {/* Vitals Section */}
            <div className="row mb-3">
              <div className="col-12">
                <h4 className="mb-3">Vitals</h4>
                {formData.vitals.map((vital, vitalIndex) => (
                  <div key={`vitals-${vitalIndex}`} className="mb-4">
                    {formData.vitals.length > 1 && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="m-0">{vital.sequenceNumber}:</h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeVitalsRecord(vitalIndex)}
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    {/* First row of vitals */}
                    <div className="row mb-3">
                    <div className="col-md-2">
  <label>Height<span className="text-red"> *</span></label>
  <div className="input-group">
    <input
      type="text"
      className={`form-control ${vitalErrors.height ? "is-invalid" : ""}`}
      name="height"
      value={vital.height}
      onChange={(e) => handleVitalsChange(e, vitalIndex)}
      onBlur={() => calculateBMIAndBSA(vitalIndex)}
    />
    <span className="input-group-text">cm</span>
  </div>
  {vitalErrors.height && (
    <div className="invalid-feedback d-block">{vitalErrors.height}</div>
  )}
</div>
                      {/* <div className="col-md-2">
                        <label>Height<span className="text-red"> *</span></label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="height"
                            value={vital.height}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                            onBlur={() => calculateBMIAndBSA(vitalIndex)}
                          />
                          <span className="input-group-text">cm</span>
                        </div>
                      </div> */}
                      {/* <div className="col-md-2">
                        <label>Weight<span className="text-red"> *</span></label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="weight"
                            value={vital.weight}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                            onBlur={() => calculateBMIAndBSA(vitalIndex)}
                          />
                          <span className="input-group-text">kgs</span>
                        </div>
                      </div> */}
                      {/* // Update the weight field: */}
<div className="col-md-2">
  <label>Weight<span className="text-red"> *</span></label>
  <div className="input-group">
    <input
      type="text"
      className={`form-control ${vitalErrors.weight ? "is-invalid" : ""}`}
      name="weight"
      value={vital.weight}
      onChange={(e) => handleVitalsChange(e, vitalIndex)}
      onBlur={() => calculateBMIAndBSA(vitalIndex)}
    />
    <span className="input-group-text">kgs</span>
  </div>
  {vitalErrors.weight && (
    <div className="invalid-feedback d-block">{vitalErrors.weight}</div>
  )}
</div>
                      <div className="col-md-2">
                        <label>Pulse</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="pulse"
                            value={vital.pulse}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          />
                          <span className="input-group-text">bpm</span>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>Temperature</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="temperature"
                            value={vital.temperature}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          />
                          <span className="input-group-text">F</span>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>BP</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="bp"
                            value={vital.bp}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                            placeholder="Sys/Dia"
                          />
                          {vital.bp && vital.bp.trim() !== '' && (
                            <span className="input-group-text">mmHg</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>SpO2</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="spo2"
                            value={vital.spo2}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>
                    </div>

                    {/* Second row of vitals */}
                    <div className="row mb-3">
                      <div className="col-md-2">
                        <label>Respiratory Rate</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="respiratoryRate"
                            value={vital.respiratoryRate}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          />
                          <span className="input-group-text">BRPM</span>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>Blood Glucose</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="bloodGlucose"
                            value={vital.bloodGlucose}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          />
                          <span className="input-group-text">mg/dL</span>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>Glucose Test Type</label>
                        <select
                          className="form-control"
                          name="glucoseTestType"
                          value={vital.glucoseTestType}
                          onChange={(e) => handleVitalsChange(e, vitalIndex)}
                        >
                          <option value=""></option>
                          <option value="Fasting">Fasting</option>
                          <option value="Random">Random</option>
                          <option value="Pre Meal">Pre Meal</option>
                          <option value="Post Meal">Post Meal</option>
                          <option value="Before Sleep">Before Sleep</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label>BSA</label>
                        <input
                          type="text"
                          className="form-control"
                          name="bsa"
                          value={vital.bsa}
                          onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          readOnly
                        />
                      </div>
                      <div className="col-md-2">
                        <label>BMI</label>
                        <input
                          type="text"
                          className="form-control"
                          name="bmi"
                          value={vital.bmi}
                          onChange={(e) => handleVitalsChange(e, vitalIndex)}
                          readOnly
                        />
                      </div>
                      <div className="col-md-2">
                        <label>Record Time</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="recordTime"
                            value={vital.recordTime}
                            onChange={(e) => handleVitalsChange(e, vitalIndex)}
                            placeholder="hh:mm aa"
                          />
                          <span className="input-group-text">
                            <i className="fas fa-clock"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    {vitalIndex < formData.vitals.length - 1 && (
                      <hr className="mt-4 mb-4" />
                    )}
                  </div>
                ))}
                <div className="mt-3">
                  <Button
                    className="btn btn-secondary"
                    onClick={addVitalsRecord}
                  >
                    Add More Vitals
                  </Button>
                </div>
              </div>
            </div>

            {/* Chief Complaints/Symptoms Section with Fixed Duration Dropdown */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Chief Complaints/Symptoms</strong>
                  <Button variant="link" onClick={() => loadPrevious('complaints')}>
                    Load Previous
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered border">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>COMPLAINTS</th>
                        <th>FREQUENCY</th>
                        <th>SEVERITY</th>
                        <th>DURATION</th>
                        <th>DATE</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.complaints.map((complaint, index) => (
                        <tr
                          key={`complaint-${index}`}
                          className={isComplaintRowFilled(index) ? "row-with-content" : ""}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <Select
                              className="w-100"
                              placeholder="Select Complaint"
                              value={complaint.complaint || undefined}
                              onChange={(value, option) => {
                                handleComplaintChange(index, "complaint", value);
                                // Close dropdown after selection
                                setOpenComplaintDropdownIndex(-1);
                              }}
                              loading={loadingComplaints}
                              showSearch
                              open={openComplaintDropdownIndex === index}
                              onDropdownVisibleChange={(open) => {
                                setOpenComplaintDropdownIndex(open ? index : -1);
                              }}
                              filterOption={(input, option) => {
                                if (!input) return true;
                                return option.children.toLowerCase().includes(input.toLowerCase());
                              }}
                              onSearch={(value) => {
                                setNewItemInput(prev => ({ ...prev, complaint: value }));
                              }}
                              notFoundContent={
                                newItemInput.complaint ? (
                                  <div
                                    style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                                    onClick={() => {
                                      if (newItemInput.complaint.trim()) {
                                        // Show loading indicator
                                        setLoadingComplaints(true);

                                        // Close the dropdown while saving
                                        setOpenComplaintDropdownIndex(-1);

                                        // Store the complaint name for later use
                                        const complaintName = newItemInput.complaint.trim();

                                        saveComplaint(complaintName)
                                          .then(result => {
                                            if (result) {
                                              // Update the complaint in the current row
                                              handleComplaintChange(index, "complaint", result.name);

                                              // Clear the search input
                                              setNewItemInput({ ...newItemInput, complaint: "" });

                                              // Show success message as a popup
                                              swal({
                                                title: "Success!",
                                                text: `Complaint "${complaintName}" added successfully`,
                                                icon: "success",
                                                buttons: false,
                                                timer: 2000,
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            swal({
                                              title: "Error!",
                                              text: "Failed to add complaint. Please try again.",
                                              icon: "error",
                                              buttons: false,
                                              timer: 2000,
                                            });
                                          })
                                          .finally(() => {
                                            setLoadingComplaints(false);
                                          });
                                      }
                                    }}
                                  >
                                    Create "{newItemInput.complaint}"
                                  </div>
                                ) : (
                                  <div style={{ padding: '5px' }}>No data found</div>
                                )
                              }
                            >
                              {complaintsArr.map((complaint, idx) => (
                                <Option key={complaint.id || `complaint-${idx}`} value={complaint.name}>
                                  {complaint.name}
                                </Option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <Select
                              className="w-100"
                              placeholder="Select Frequency"
                              value={complaint.frequency || undefined}
                              onChange={(value, option) => {
                                handleComplaintChange(index, "frequency", value);
                                // Close dropdown after selection
                                setOpenFrequencyDropdownIndex(-1);
                              }}
                              loading={loadingFrequencies}
                              showSearch
                              open={openFrequencyDropdownIndex === index}
                              onDropdownVisibleChange={(open) => {
                                setOpenFrequencyDropdownIndex(open ? index : -1);
                              }}
                              filterOption={(input, option) => {
                                if (!input) return true;
                                return option.children.toLowerCase().includes(input.toLowerCase());
                              }}
                              onSearch={(value) => {
                                setNewItemInput(prev => ({ ...prev, frequency: value }));
                              }}
                              notFoundContent={
                                newItemInput.frequency ? (
                                  <div
                                    style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                                    onClick={() => {
                                      if (newItemInput.frequency.trim()) {
                                        // Show loading indicator
                                        setLoadingFrequencies(true);

                                        // Close the dropdown while saving
                                        setOpenFrequencyDropdownIndex(-1);

                                        // Store the frequency name for later use
                                        const frequencyName = newItemInput.frequency.trim();

                                        saveFrequency(frequencyName)
                                          .then(result => {
                                            if (result) {
                                              // Update the frequency in the current row
                                              handleComplaintChange(index, "frequency", result.name);

                                              // Clear the search input
                                              setNewItemInput({ ...newItemInput, frequency: "" });

                                              // Show success message as a popup
                                              swal({
                                                title: "Success!",
                                                text: `Frequency "${frequencyName}" added successfully`,
                                                icon: "success",
                                                buttons: false,
                                                timer: 2000,
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            swal({
                                              title: "Error!",
                                              text: "Failed to add frequency. Please try again.",
                                              icon: "error",
                                              buttons: false,
                                              timer: 2000,
                                            });
                                          })
                                          .finally(() => {
                                            setLoadingFrequencies(false);
                                          });
                                      }
                                    }}
                                  >
                                    Create "{newItemInput.frequency}"
                                  </div>
                                ) : (
                                  <div style={{ padding: '5px' }}>No data found</div>
                                )
                              }
                            >
                              {frequenciesArr.map((frequency, idx) => (
                                <Option key={frequency.id || `frequency-${idx}`} value={frequency.name}>
                                  {frequency.name}
                                </Option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <Select
                              className="w-100"
                              placeholder="Select Severity"
                              value={complaint.severity || undefined}
                              onChange={(value, option) => {
                                handleComplaintChange(index, "severity", value);
                                // Close dropdown after selection
                                setOpenSeverityDropdownIndex(-1);
                              }}
                              loading={loadingSeverities}
                              showSearch
                              open={openSeverityDropdownIndex === index}
                              onDropdownVisibleChange={(open) => {
                                setOpenSeverityDropdownIndex(open ? index : -1);
                              }}
                              filterOption={(input, option) => {
                                if (!input) return true;
                                return option.children.toLowerCase().includes(input.toLowerCase());
                              }}
                              onSearch={(value) => {
                                setNewItemInput(prev => ({ ...prev, severity: value }));
                              }}
                              notFoundContent={
                                newItemInput.severity ? (
                                  <div
                                    style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                                    onClick={() => {
                                      if (newItemInput.severity.trim()) {
                                        // Show loading indicator
                                        setLoadingSeverities(true);

                                        // Close the dropdown while saving
                                        setOpenSeverityDropdownIndex(-1);

                                        // Store the severity name for later use
                                        const severityName = newItemInput.severity.trim();

                                        saveSeverity(severityName)
                                          .then(result => {
                                            if (result) {
                                              // Update the severity in the current row
                                              handleComplaintChange(index, "severity", result.name);

                                              // Clear the search input
                                              setNewItemInput({ ...newItemInput, severity: "" });

                                              // Show success message as a popup
                                              swal({
                                                title: "Success!",
                                                text: `Severity "${severityName}" added successfully`,
                                                icon: "success",
                                                buttons: false,
                                                timer: 2000,
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            swal({
                                              title: "Error!",
                                              text: "Failed to add severity. Please try again.",
                                              icon: "error",
                                              buttons: false,
                                              timer: 2000,
                                            });
                                          })
                                          .finally(() => {
                                            setLoadingSeverities(false);
                                          });
                                      }
                                    }}
                                  >
                                    Create "{newItemInput.severity}"
                                  </div>
                                ) : (
                                  <div style={{ padding: '5px' }}>No data found</div>
                                )
                              }
                            >
                              {severitiesArr.map((severity, idx) => (
                                <Option key={severity.id || `severity-${idx}`} value={severity.name}>
                                  {severity.name}
                                </Option>
                              ))}
                            </Select>
                          </td>

                          <td>
                            <div className="d-flex">
                              {/* Input for duration value */}
                              <input
                                type="text"
                                className="form-control"
                                value={complaint.durationValue || ""}
                                onChange={(e) => {
                                  // Extract the numeric value from the input
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  handleComplaintChange(index, "durationValue", value);
                                }}
                                placeholder="e.g., 20"
                                style={{ flexGrow: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                              />

                              {/* Dropdown for duration unit */}
                              <select
                                className="form-control"
                                value={complaint.durationUnit || "Days"}
                                onChange={(e) => {
                                  handleDurationUnitSelect(index, e.target.value);
                                }}
                                style={{ width: "auto", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                              >
                                <option value="Days">Days</option>
                                <option value="Weeks">Weeks</option>
                                <option value="Months">Months</option>
                                <option value="Years">Years</option>
                              </select>
                            </div>
                          </td>
                          {/* <td>
                            <input
                              type="date"
                              className="form-control"
                              value={complaint.date || getCurrentDate()} // Fallback to current date
                              onChange={(e) => handleComplaintChange(index, "date", e.target.value)}
                            />
                          </td> */}
                          {/* // For Chief Complaints/Symptoms date fields in the table */}
<td>
  <DatePicker
    format={dateFormat}
    value={complaint.date ? moment(complaint.date, dateFormat1) : moment()} // Show today by default
    onChange={(date, dateString) => {
      const formattedDate = date ? formatDateForInput(date.toDate()) : '';
      handleComplaintChange(index, "date", formattedDate);
    }}
    style={{ width: '100%' }}
  />
</td>
                          <td>
                            <span
                              className="delete-icon1"
                              onClick={() => removeComplaintRow(index)}
                              style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
                            >
                              ✕
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* History of Present Illness Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>History of Present Illness</strong>
                  <Button variant="link" onClick={() => loadPrevious('history')}>
                    Load Previous
                  </Button>
                </div>
                <textarea
                  className="form-control"
                  rows="5"
                  name="historyOfPresentIllness"
                  value={formData.historyOfPresentIllness}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>

            {/* Clinical Assessment Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Clinical Assessment</strong>
                  <Button variant="link" onClick={() => loadPrevious('assessment')}>
                    Load Previous
                  </Button>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>Status of the disease</label>
                    <Select
                      className="w-100"
                      placeholder="Select Status"
                      value={formData.clinicalAssessment.statusOfDisease || undefined}
                      onChange={(value, option) => {
                        handleClinicalAssessmentChange({ target: { name: 'statusOfDisease', value } });
                        // Close dropdown after selection
                        setOpenDiseaseStatusDropdownIndex(-1);
                      }}
                      loading={loadingDiseaseStatus}
                      showSearch
                      open={openDiseaseStatusDropdownIndex === 0}
                      onDropdownVisibleChange={(open) => {
                        setOpenDiseaseStatusDropdownIndex(open ? 0 : -1);
                      }}
                      filterOption={(input, option) => {
                        if (!input) return true;
                        return option.children.toLowerCase().includes(input.toLowerCase());
                      }}
                      onSearch={(value) => {
                        setNewItemInput(prev => ({ ...prev, statusOfDisease: value }));
                      }}
                      notFoundContent={
                        newItemInput.statusOfDisease ? (
                          <div
                            style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                            onClick={() => {
                              if (newItemInput.statusOfDisease.trim()) {
                                // Show loading indicator
                                setLoadingDiseaseStatus(true);

                                // Close the dropdown while saving
                                setOpenDiseaseStatusDropdownIndex(-1);

                                // Store the status name for later use
                                const statusName = newItemInput.statusOfDisease.trim();

                                saveDiseaseStatus(statusName)
                                  .then(result => {
                                    if (result) {
                                      // Update the status in the current row
                                      handleClinicalAssessmentChange({ target: { name: 'statusOfDisease', value: result.name } });

                                      // Clear the search input
                                      setNewItemInput({ ...newItemInput, statusOfDisease: "" });

                                      // Show success message as a popup
                                      swal({
                                        title: "Success!",
                                        text: `Status "${statusName}" added successfully`,
                                        icon: "success",
                                        buttons: false,
                                        timer: 2000,
                                      });
                                    }
                                  })
                                  .catch(error => {
                                    swal({
                                      title: "Error!",
                                      text: "Failed to add status. Please try again.",
                                      icon: "error",
                                      buttons: false,
                                      timer: 2000,
                                    });
                                  })
                                  .finally(() => {
                                    setLoadingDiseaseStatus(false);
                                  });
                              }
                            }}
                          >
                            Create "{newItemInput.statusOfDisease}"
                          </div>
                        ) : (
                          <div style={{ padding: '5px' }}>No data found</div>
                        )
                      }
                    >
                      {diseaseStatusArr.map((status, idx) => (
                        <Option key={status.id || `status-${idx}`} value={status.name}>
                          {status.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-md-4">
                    <label>ECOG</label>
                    <Select
                      className="form-control"
                      value={ecogOptions.find(option => option.value === formData.clinicalAssessment.ecog) || null}
                      onChange={handleSelectChange}
                      options={ecogOptions}
                      placeholder="Select ECOG"
                    />
                  </div>
                  <div className="col-md-4">
                    <label>KPS</label>
                    <Select
                      className="form-control"
                      value={kpsOptions.find(option => option.value === formData.clinicalAssessment.kps) || null}
                      onChange={handleSelectChange1}
                      options={kpsOptions}
                      placeholder="Select KPS"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label>On Examination</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      name="onExamination"
                      value={formData.clinicalAssessment.onExamination}
                      onChange={handleClinicalAssessmentChange}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Diagnosis Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Secondary Diagnosis</strong>
                  <div>
                    <Button variant="link" onClick={() => loadPrevious('secondaryDiagnosis')}>
                      Load Previous
                    </Button>
                    {/* <Button variant="link">
          QuickList
        </Button> */}
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered border">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>DIAGNOSIS</th>
                        <th>DURATION</th>
                        <th>COMMENTS</th>
                        <th>DATE</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.secondaryDiagnosis.map((diagnosis, index) => (
                        <tr
                          key={`diagnosis-${index}`}
                          className={isDiagnosisRowFilled(index) ? "row-with-content" : ""}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <Select
                              className="w-100"
                              placeholder="Select Diagnosis"
                              value={diagnosis.diagnosis || undefined}
                              onChange={(value, option) => {
                                handleSecondaryDiagnosisChange(index, "diagnosis", value);
                                // Close dropdown after selection
                                setOpenDiagnosisDropdownIndex(-1);
                              }}
                              loading={loadingDiagnosis}
                              showSearch
                              open={openDiagnosisDropdownIndex === index}
                              onDropdownVisibleChange={(open) => {
                                setOpenDiagnosisDropdownIndex(open ? index : -1);
                              }}
                              filterOption={(input, option) => {
                                if (!input) return true;
                                return option.children.toLowerCase().includes(input.toLowerCase());
                              }}
                              onSearch={(value) => {
                                setNewItemInput(prev => ({ ...prev, diagnosis: value }));
                              }}
                              notFoundContent={
                                newItemInput.diagnosis ? (
                                  <div
                                    style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                                    onClick={() => {
                                      if (newItemInput.diagnosis.trim()) {
                                        // Show loading indicator
                                        setLoadingDiagnosis(true);

                                        // Close the dropdown while saving
                                        setOpenDiagnosisDropdownIndex(-1);

                                        // Store the diagnosis name for later use
                                        const diagnosisName = newItemInput.diagnosis.trim();

                                        saveDiagnosis(diagnosisName)
                                          .then(result => {
                                            if (result) {
                                              // Update the diagnosis in the current row
                                              handleSecondaryDiagnosisChange(index, "diagnosis", result.name);

                                              // Clear the search input
                                              setNewItemInput({ ...newItemInput, diagnosis: "" });

                                              // Show success message as a popup
                                              swal({
                                                title: "Success!",
                                                text: `Diagnosis "${diagnosisName}" added successfully`,
                                                icon: "success",
                                                buttons: false,
                                                timer: 2000,
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            swal({
                                              title: "Error!",
                                              text: "Failed to add diagnosis. Please try again.",
                                              icon: "error",
                                              buttons: false,
                                              timer: 2000,
                                            });
                                          })
                                          .finally(() => {
                                            setLoadingDiagnosis(false);
                                          });
                                      }
                                    }}
                                  >
                                    Create "{newItemInput.diagnosis}"
                                  </div>
                                ) : (
                                  <div style={{ padding: '5px' }}>No data found</div>
                                )
                              }
                            >
                              {diagnosisArr.map((diagnosis, idx) => (
                                <Option key={diagnosis.id || `diagnosis-${idx}`} value={diagnosis.name}>
                                  {diagnosis.name}
                                </Option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <div className="d-flex">
                              {/* Input for duration value */}
                              <input
                                type="text"
                                className="form-control"
                                value={diagnosis.durationValue || ""}
                                onChange={(e) => {
                                  // Extract the numeric value from the input
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  handleSecondaryDiagnosisChange(index, "durationValue", value);
                                }}
                                placeholder="e.g., 20"
                                style={{ flexGrow: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                              />

                              {/* Dropdown for duration unit */}
                              <select
                                className="form-control"
                                value={diagnosis.durationUnit || "Days"}
                                onChange={(e) => {
                                  handleSecondaryDiagnosisDurationUnitSelect(index, e.target.value);
                                }}
                                style={{ width: "auto", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                              >
                                <option value="Days">Days</option>
                                <option value="Weeks">Weeks</option>
                                <option value="Months">Months</option>
                                <option value="Years">Years</option>
                              </select>
                            </div>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={diagnosis.comments}
                              onChange={(e) => handleSecondaryDiagnosisChange(index, "comments", e.target.value)}
                            />
                          </td>
                          {/* <td>
                            <input
                              type="date"
                              className="form-control"
                              value={diagnosis.date || getCurrentDate()} // Fallback to current date
                              onChange={(e) => handleSecondaryDiagnosisChange(index, "date", e.target.value)}
                            />
                          </td> */}
                          {/* // For Secondary Diagnosis date fields in the table */}
<td>
  <DatePicker
    format={dateFormat}
    value={diagnosis.date ? moment(diagnosis.date, dateFormat1) : moment()} // Show today by default
    onChange={(date, dateString) => {
      const formattedDate = date ? formatDateForInput(date.toDate()) : '';
      handleSecondaryDiagnosisChange(index, "date", formattedDate);
    }}
    style={{ width: '100%' }}
  />
</td>
                          <td>
                            <span
                              className="delete-icon1"
                              onClick={() => removeSecondaryDiagnosisRow(index)}
                              style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
                            >
                              ✕
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

           {/* Doctor Prescription Section - UPDATED */}
{/* Doctor Prescription Section - Complete Code */}
<div className="row mb-3 prescription-section">
  <div className="col-12">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <strong className="mb-3">Doctor Prescription</strong>
      <div>
        <Button variant="link" onClick={() => loadPrevious('prescription')}>
          Load Previous
        </Button>
      </div>
    </div>
    <div className="table-responsive">
      <table className="table table-bordered border">
        <thead>
          <tr>
            <th>#</th>
            <th>MEDICINE<span className="text-danger"></span></th>
            <th>DOSE<span className="text-danger"></span></th>
            <th>WHEN<span className="text-danger"></span></th>
            <th>DURATION<span className="text-danger"></span></th>
            <th>INSTRUCTIONS<span className="text-danger"></span></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {formData.prescription.map((med, index) => (
            <tr
              key={`med-${index}`}
              className={isPrescriptionRowFilled(index) ? "row-with-content" : ""}
            >
              <td>{index + 1}</td>
              <td>
                {/* Medicine dropdown */}
                <Select
                  className={`w-100 }`}
                  placeholder="Select Medicine"
                  value={med.medicine || undefined}
                  onChange={(value, option) => {
                    handlePrescriptionChange(index, "medicine", value);
                    // Close dropdown after selection
                    setOpenMedicineDropdownIndex(-1);
                  }}
                  loading={loadingMedicines}
                  showSearch
                  open={openMedicineDropdownIndex === index}
                  onDropdownVisibleChange={(open) => {
                    setOpenMedicineDropdownIndex(open ? index : -1);
                  }}
                  filterOption={(input, option) => {
                    if (!input) return true;
                    return option.children.toLowerCase().includes(input.toLowerCase());
                  }}
                  onSearch={(value) => {
                    setNewItemInput(prev => ({ ...prev, medicine: value }));
                  }}
                  notFoundContent={
                    newItemInput.medicine ? (
                      <div
                        style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (newItemInput.medicine.trim()) {
                            saveMedicine(newItemInput.medicine.trim())
                              .then(result => {
                                if (result) {
                                  handlePrescriptionChange(index, "medicine", result.name);
                                  setNewItemInput(prev => ({ ...prev, medicine: "" }));
                                }
                              })
                              .catch(error => {
                                swal({
                                  title: "Error!",
                                  text: "Failed to add medicine. Please try again.",
                                  icon: "error",
                                  buttons: false,
                                  timer: 2000,
                                });
                              });
                          }
                        }}
                      >
                        Create "{newItemInput.medicine}"
                      </div>
                    ) : (
                      <div style={{ padding: '5px' }}>No data found</div>
                    )
                  }
                >
                  {medicinesArr.map((medicine, idx) => (
                    <Option key={medicine.id || `medicine-${idx}`} value={medicine.name} data-medicineid={medicine.medicineId}>
                      {medicine.name}
                    </Option>
                  ))}
                </Select>
                {medicationErrors[index]?.medicine && (
                  <div className="invalid-feedback d-block">{medicationErrors[index].medicine}</div>
                )}
              </td>
              <td>
                {/* Dose dropdown */}
                <Select
                  className={`w-100 }`}
                  placeholder="Select Dose"
                  value={med.dose || undefined}
                  onChange={(value, option) => {
                    handlePrescriptionChange(index, "dose", value);
                    // Close dropdown after selection
                    setOpenDoseDropdownIndex(-1);
                  }}
                  loading={loadingDoses}
                  showSearch
                  open={openDoseDropdownIndex === index}
                  onDropdownVisibleChange={(open) => {
                    setOpenDoseDropdownIndex(open ? index : -1);
                  }}
                  filterOption={(input, option) => {
                    if (!input) return true;
                    return option.children.toLowerCase().includes(input.toLowerCase());
                  }}
                  onSearch={(value) => {
                    setNewItemInput(prev => ({ ...prev, dose: value }));
                  }}
                  notFoundContent={
                    newItemInput.dose ? (
                      <div
                        style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                        onClick={() => {
                          if (newItemInput.dose.trim()) {
                            // Show loading indicator
                            setLoadingDoses(true);

                            // Close the dropdown while saving
                            setOpenDoseDropdownIndex(-1);

                            // Store the dose name for later use
                            const doseName = newItemInput.dose.trim();

                            saveDose(doseName)
                              .then(result => {
                                if (result) {
                                  // Update the dose in the current row
                                  handlePrescriptionChange(index, "dose", result.name);

                                  // Clear the search input
                                  setNewItemInput({ ...newItemInput, dose: "" });

                                  // Show success message as a popup
                                  swal({
                                    title: "Success!",
                                    text: `Dose "${doseName}" added successfully`,
                                    icon: "success",
                                    buttons: false,
                                    timer: 2000,
                                  });
                                }
                              })
                              .catch(error => {
                                swal({
                                  title: "Error!",
                                  text: "Failed to add dose. Please try again.",
                                  icon: "error",
                                  buttons: false,
                                  timer: 2000,
                                });
                              })
                              .finally(() => {
                                setLoadingDoses(false);
                              });
                          }
                        }}
                      >
                        Create "{newItemInput.dose}"
                      </div>
                    ) : (
                      <div style={{ padding: '5px' }}>No data found</div>
                    )
                  }
                >
                  {dosesArr.map((dose, idx) => (
                    <Option key={dose.id || `dose-${idx}`} value={dose.name}>
                      {dose.name}
                    </Option>
                  ))}
                </Select>
                {medicationErrors[index]?.dose && (
                  <div className="invalid-feedback d-block">{medicationErrors[index].dose}</div>
                )}
              </td>
              <td>
                {/* When dropdown */}
                <Select
                  className={`w-100 }`}
                  placeholder="Select When"
                  value={med.when || undefined}
                  onChange={(value, option) => {
                    handlePrescriptionChange(index, "when", value);
                    // Close dropdown after selection
                    setOpenWhenDropdownIndex(-1);
                  }}
                  loading={loadingWhens}
                  showSearch
                  open={openWhenDropdownIndex === index}
                  onDropdownVisibleChange={(open) => {
                    setOpenWhenDropdownIndex(open ? index : -1);
                  }}
                  filterOption={(input, option) => {
                    if (!input) return true;
                    return option.children.toLowerCase().includes(input.toLowerCase());
                  }}
                  onSearch={(value) => {
                    setNewItemInput(prev => ({ ...prev, when: value }));
                  }}
                  notFoundContent={
                    newItemInput.when ? (
                      <div
                        style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                        onClick={() => {
                          if (newItemInput.when.trim()) {
                            // Show loading indicator
                            setLoadingWhens(true);

                            // Close the dropdown while saving
                            setOpenWhenDropdownIndex(-1);

                            // Store the when name for later use
                            const whenName = newItemInput.when.trim();

                            saveWhen(whenName)
                              .then(result => {
                                if (result) {
                                  // Update the when in the current row
                                  handlePrescriptionChange(index, "when", result.name);

                                  // Clear the search input
                                  setNewItemInput({ ...newItemInput, when: "" });

                                  // Show success message as a popup
                                  swal({
                                    title: "Success!",
                                    text: `When "${whenName}" added successfully`,
                                    icon: "success",
                                    buttons: false,
                                    timer: 2000,
                                  });
                                }
                              })
                              .catch(error => {
                                swal({
                                  title: "Error!",
                                  text: "Failed to add when. Please try again.",
                                  icon: "error",
                                  buttons: false,
                                  timer: 2000,
                                });
                              })
                              .finally(() => {
                                setLoadingWhens(false);
                              });
                          }
                        }}
                      >
                        Create "{newItemInput.when}"
                      </div>
                    ) : (
                      <div style={{ padding: '5px' }}>No data found</div>
                    )
                  }
                >
                  {whensArr.map((when, idx) => (
                    <Option key={when.id || `when-${idx}`} value={when.name}>
                      {when.name}
                    </Option>
                  ))}
                </Select>
                {medicationErrors[index]?.when && (
                  <div className="invalid-feedback d-block">{medicationErrors[index].when}</div>
                )}
              </td>
              <td>
                {/* Duration with value and unit */}
                <div className="d-flex">
                  {/* Input for duration value */}
                  <input
                    type="text"
                    className={`form-control }`}
                    value={med.durationValue || ""}
                    onChange={(e) => {
                      // Extract the numeric value from the input
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handlePrescriptionChange(index, "durationValue", value);
                    }}
                    placeholder="e.g., 20"
                    style={{ flexGrow: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  />

                  {/* Dropdown for duration unit */}
                  <select
                    className={`form-control ${medicationErrors[index]?.duration ? 'is-invalid' : ''}`}
                    value={med.durationUnit || "Days"}
                    onChange={(e) => {
                      handlePrescriptionDurationUnitSelect(index, e.target.value);
                    }}
                    style={{ width: "auto", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    <option value="Days">Days</option>
                    <option value="Weeks">Weeks</option>
                    <option value="Months">Months</option>
                    <option value="Years">Years</option>
                    <option value="Daily">Daily</option>
                  </select>
                </div>
                {medicationErrors[index]?.duration && (
                  <div className="invalid-feedback d-block">{medicationErrors[index].duration}</div>
                )}
              </td>
              <td>
                {/* Instructions input */}
                <Form.Control
                  type="text"
                  value={med.instructions || ''}
                  onChange={(e) => handlePrescriptionChange(index, 'instructions', e.target.value)}
                  placeholder="Add instructions"
                 
                />
                {medicationErrors[index]?.instructions && (
                  <div className="invalid-feedback d-block">{medicationErrors[index].instructions}</div>
                )}
              </td>
              <td>
                {/* Delete button */}
                <span
                  className="delete-icon1"
                  onClick={() => removePrescriptionRow(index)}
                  style={{
                    cursor: 'pointer',
                    color: '#dc3545',
                    fontSize: '18px',
                    padding: '4px 8px'
                  }}
                >
                  ✕
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* Note: Removed "Save Medicines" button - now everything saves with common Save button */}
  </div>
</div>

            {/* Treatment Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Treatment</strong>
                  <Button variant="link" onClick={() => loadPrevious('treatment')}>
                    Load Previous
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered border">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>TREATMENT TYPE</th>
                        <th>DESCRIPTION</th>
                        <th>DATE</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.treatment.map((treatment, index) => (
                        <tr
                          key={`treatment-${index}`}
                          className={isTreatmentRowFilled(index) ? "row-with-content" : ""}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <Select
                              className="w-100"
                              placeholder="Select Treatment Type"
                              value={treatment.treatmentType || undefined}
                              onChange={(value, option) => {
                                handleTreatmentChange(index, "treatmentType", value);
                                // Close dropdown after selection
                                setOpenTreatmentTypeDropdownIndex(-1);
                              }}
                              loading={loadingTreatmentTypes}
                              showSearch
                              open={openTreatmentTypeDropdownIndex === index}
                              onDropdownVisibleChange={(open) => {
                                setOpenTreatmentTypeDropdownIndex(open ? index : -1);
                              }}
                              filterOption={(input, option) => {
                                if (!input) return true;
                                return option.children.toLowerCase().includes(input.toLowerCase());
                              }}
                              onSearch={(value) => {
                                setNewItemInput(prev => ({ ...prev, treatmentType: value }));
                              }}
                              notFoundContent={
                                newItemInput.treatmentType ? (
                                  <div
                                    style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                                    onClick={() => {
                                      if (newItemInput.treatmentType.trim()) {
                                        // Show loading indicator
                                        setLoadingTreatmentTypes(true);

                                        // Close the dropdown while saving
                                        setOpenTreatmentTypeDropdownIndex(-1);

                                        // Store the treatment type name for later use
                                        const treatmentTypeName = newItemInput.treatmentType.trim();

                                        saveTreatmentType(treatmentTypeName)
                                          .then(result => {
                                            if (result) {
                                              // Update the treatment type in the current row
                                              handleTreatmentChange(index, "treatmentType", result.name);

                                              // Clear the search input
                                              setNewItemInput({ ...newItemInput, treatmentType: "" });

                                              // Show success message as a popup
                                              swal({
                                                title: "Success!",
                                                text: `Treatment Type "${treatmentTypeName}" added successfully`,
                                                icon: "success",
                                                buttons: false,
                                                timer: 2000,
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            swal({
                                              title: "Error!",
                                              text: "Failed to add treatment type. Please try again.",
                                              icon: "error",
                                              buttons: false,
                                              timer: 2000,
                                            });
                                          })
                                          .finally(() => {
                                            setLoadingTreatmentTypes(false);
                                          });
                                      }
                                    }}
                                  >
                                    Create "{newItemInput.treatmentType}"
                                  </div>
                                ) : (
                                  <div style={{ padding: '5px' }}>No data found</div>
                                )
                              }
                            >
                              {treatmentTypesArr.map((treatmentType, idx) => (
                                <Option key={treatmentType.id || `treatmentType-${idx}`} value={treatmentType.name}>
                                  {treatmentType.name}
                                </Option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={treatment.description}
                              onChange={(e) => handleTreatmentChange(index, "description", e.target.value)}
                            />
                          </td>
                          {/* <td>
                            <input
                              type="date"
                              className="form-control"
                              value={treatment.date || getCurrentDate()} // Fallback to current date
                              onChange={(e) => handleTreatmentChange(index, "date", e.target.value)}
                            />
                          </td> */}
                          <td>
                          <DatePicker
  format={dateFormat}
  value={treatment.date ? moment(treatment.date, dateFormat1) : moment()} // Use moment() for today's date as default
  onChange={(date, dateString) => {
    const formattedDate = date ? formatDateForInput(date.toDate()) : '';
    handleTreatmentChange(index, "date", formattedDate);
  }}
  style={{ width: '100%' }}
/>
</td>
                          <td>
                            <span
                              className="delete-icon1"
                              onClick={() => removeTreatmentRow(index)}
                              style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
                            >
                              ✕
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Advice/Notes Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-3">Advice/Notes</h4>
                  {/* <div>
                    <Button variant="link">
                      Save To List
                    </Button>
                    <Button variant="link">
                      Options
                    </Button>
                  </div> */}
                </div>
                <textarea
                  className="form-control"
                  rows="4"
                  name="adviceNotes"
                  value={formData.adviceNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>


            {/* Tests Requested Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Tests Requested</strong>
                </div>
                <div className="test-selector-container" style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => handleSaveToTestList()}>
                      Save To List
                    </Button>
                  </div>

                  {/* Selected tests display */}
                  <div className="selected-tests mb-3">
                    {selectedTests.map((test, index) => (
                      <span
                        key={`selected-test-${index}`}
                        className="selected-test-badge"
                        style={{
                          display: 'inline-block',
                          backgroundColor: '#f8f9fa',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          margin: '0 5px 5px 0',
                          border: '1px solid #dee2e6'
                        }}
                      >
                        {test}
                        <span
                          className="remove-test"
                          style={{
                            marginLeft: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            color: '#dc3545'
                          }}
                          onClick={() => handleRemoveTest(test)}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                  </div>

                  {/* Search input for tests */}
                  <input
                    type="text"
                    className="form-control mb-3"
                    placeholder="Choose"
                    value={testSearchInput}
                    onChange={(e) => setTestSearchInput(e.target.value)}
                    onFocus={() => setShowTestOptions(true)}
                  />

                  {/* Test options selection area */}
                  {showTestOptions && (
                    <div className="test-options-container" style={{
                      maxHeight: '250px',
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      position: 'relative'
                    }}>
                      <div className="test-options-list" style={{ padding: '5px' }}>
                        {testOptions.filter(option =>
                          option.toLowerCase().includes(testSearchInput.toLowerCase())
                        ).map((option, index) => (
                          <div
                            key={`test-option-${index}`}
                            className={`test-option ${selectedTests.includes(option) ? 'selected' : ''}`}
                            style={{
                              padding: '8px 12px',
                              margin: '5px 0',
                              backgroundColor: selectedTests.includes(option) ? '#eaeaea' : 'transparent',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'inline-block',
                              marginRight: '5px',
                              marginBottom: '5px',
                              border: '1px solid #dee2e6'
                            }}
                            onClick={() => handleTestSelection(option)}
                          >
                            {option}
                          </div>
                        ))}

                        {/* Add Create option if search input doesn't match any existing tests */}
                        {testSearchInput &&
                          testOptions.filter(option =>
                            option.toLowerCase().includes(testSearchInput.toLowerCase())
                          ).length === 0 && (
                            <div
                              className="test-option-create"
                              style={{
                                padding: '8px 12px',
                                margin: '5px 0',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #dee2e6'
                              }}
                              onClick={() => saveTestRequested(testSearchInput)}
                            >
                              Create "{testSearchInput}"
                            </div>
                          )}
                      </div>
                      <div className="scroll-indicators" style={{
                        position: 'absolute',
                        right: '5px',
                        top: '0',
                        bottom: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '5px'
                      }}>
                        <div style={{ cursor: 'pointer' }}>▲</div>
                        <div style={{ cursor: 'pointer' }}>▼</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Review notes by Primary Consultant Section */}
            <div className="row mb-3">
              <div className="col-12">
                <strong>Review notes by Primary Consultant (if any)</strong>
                <textarea
                  className="form-control"
                  rows="4"
                  name="reviewNotes"
                  value={formData.reviewNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>

            {/* Refer To Section */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Refer To:</label>
                <input
                  type="text"
                  className="form-control"
                  name="referTo"
                  value={formData.referTo}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Next Visit Date Section */}
            <div className="row mb-3">
              <div className="col-12">
                <strong>Next Visit Date</strong>
                <div className="row">
                  <div className="col-md-4">
                    {/* <input
                      type="date"
                      className="form-control"
                      name="nextVisitDate"
                      value={formData.nextVisitDate}
                      onChange={handleInputChange}
                    /> */}
                    <DatePicker
  format={dateFormat}
  value={formData.nextVisitDate ? moment(formData.nextVisitDate, dateFormat1) : null}
  onChange={(date, dateString) => handleDateChange(date, dateString, "nextVisitDate")}
  className={errors.nextVisitDate ? "is-invalid" : ""}
  style={{ width: '100%' }}
/>
                  </div>
                  <div className="d-flex">
                    {/* Input for duration value */}
                    <input
                      type="text"
                      className="form-control"
                      value={formData.nextVisitDurationValue || ""}
                      onChange={(e) => {
                        // Extract the numeric value from the input
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleNextVisitDurationChange(value);
                      }}
                      placeholder="e.g., 1"
                      style={{ flexGrow: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />

                    {/* Dropdown for duration unit */}
                    <select
                      className="form-control"
                      value={formData.nextVisitDurationUnit || "Days"}
                      onChange={(e) => {
                        handleNextVisitDurationUnitSelect(e.target.value);
                      }}
                      style={{ width: "auto", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      <option value="Days">Days</option>
                      <option value="Weeks">Weeks</option>
                      <option value="Months">Months</option>
                      <option value="Years">Years</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
                  <div className="row mt-4 mb-4">
                    <div className="col-12 text-end">
                      {/* <Button 
                        type="button"
                        className="btn btn-secondary"
                        onClick={saveOPVisit}
                        disabled={!isFormValid || saveLoader} // Disabled if vitals are invalid or loading
                      >
                        {saveLoader && (
                          <Spinner className='mr-2' animation='border' size='1.5rem' />
                        )}
                        {!saveLoader && (
                          <FaSave className='mr-2' size='1.5rem' style={{ verticalAlign: "sub" }} />
                        )}
                        Save 
                        </Button> */}
                        {/* // 5. Update the Save button to remove the isFormValid condition */}
<Button 
  type="button"
  className="btn btn-secondary"
  onClick={saveOPVisit}
  disabled={saveLoader} // Only disabled when saving is in progress
>
  {saveLoader && (
    <Spinner className='mr-2' animation='border' size='1.5rem' />
  )}
  {!saveLoader && (
    <FaSave className='mr-2' size='1.5rem' style={{ verticalAlign: "sub" }} />
  )}
  Save 
</Button>
                    </div>
                  </div>
          </form>
        </div>
      )}

      {/* OP Visit list */}
      {/* OP Visit list - Fixed Code */}
      <div className="op-visit-list-container" ref={printRef}>
        {opVisits && opVisits.length > 0 ? (
          opVisits.map((visit) => (
            <div key={visit.id || visit.visitId} className="card mb-3">
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Visit Date</strong>
                    <div>{formatDate(visit.visitDate) || "N/A"}</div>
                  </div>
                  {/* <div className="col-md-4">
                    <strong>Created By</strong>
                    <div>{visit.createdBy ||
                      (visit.createdByDTO && `${visit.createdByDTO.firstName || ''} ${visit.createdByDTO.lastName || ''}`) ||
                      (visit.doctorDTO && `${visit.doctorDTO.firstName || ''} ${visit.doctorDTO.lastName || ''}`) ||
                      "N/A"}</div>
                  </div> */}
                  <div className="col-md-4 text-end" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => printOPVisit(visit.id || visit.visitId)}
                    >
                      Print
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => showOPVisitForm(opVisits.indexOf(visit))}
                      style={{ marginLeft: '10px', }}
                    >
                      Edit
                    </button>
                  </div>
                </div>

                {/* Vitals Section */}
                {/* Vitals Section */}
                {visit.vitals && (
                  <div className="mb-4">
                    <strong>Vitals</strong>
                    {/* If vitals is an array with multiple records, display each one */}
                    {Array.isArray(visit.vitals) && visit.vitals.length > 0 ? (
                      visit.vitals.map((vital, index) => (
                        <div key={`display-vital-${index}`} className={index > 0 ? "mt-3" : ""}>
                          {visit.vitals.length > 1 && (
                            <div className="mb-2">
                              <strong>{index + 1}.</strong>
                            </div>
                          )}
                          <div className="table-responsive">
                            <table className="table table-bordered border table-sm">
                              <thead>
                                <tr>
                                  <th>Height</th>
                                  <th>Weight</th>
                                  <th>Pulse</th>
                                  <th>BP</th>
                                  <th>SpO2</th>
                                  <th>Temperature</th>
                                  <th>Respiratory Rate</th>
                                  <th>Blood Glucose</th>
                                  <th>Glucose Test Type</th>
                                  <th>BSA</th>
                                  <th>BMI</th>
                                  <th>Record Time</th>
                                </tr>
                              </thead>
                              <tbody>
                              <tr>
                                <td>{vital.height ? `${vital.height} cm` : "N/A"}</td>
                                <td>{vital.weight ? `${vital.weight} kg` : "N/A"}</td>
                                <td>{vital.pulse ? `${vital.pulse} bpm` : "N/A"}</td>
                                <td>{vital.bp ? `${vital.bp} mmHg` : "N/A"}</td>
                                <td>{vital.spo2 ? `${vital.spo2}%` : "N/A"}</td>
                                <td>{vital.temperature ? `${vital.temperature}°F` : "N/A"}</td>
                                <td>{vital.respiratoryRate ? `${vital.respiratoryRate} BRPM` : "N/A"}</td>
                                <td>
                                  {vital.bloodGlucose
                                    ? `${vital.bloodGlucose} mg/dL ${vital.glucoseTestType || ""}`
                                    : "N/A"}
                                </td>
                                <td>{vital.glucoseTestType ? vital.glucoseTestType : "N/A"}</td>
                                <td>{vital.bsa ? `${vital.bsa} m²` : "N/A"}</td>
                                <td>{vital.bmi ? `${vital.bmi} kg/m²` : "N/A"}</td>
                                <td>{vital.recordTime ? vital.recordTime : "N/A"}</td>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))
                    ) : visit.vitalsList && visit.vitalsList.length > 0 ? (
                      /* Handle direct API response format (vitalsList) */
                      visit.vitalsList.map((vital, index) => (
                        <div key={`display-vital-api-${index}`} className={index > 0 ? "mt-3" : ""}>
                          {visit.vitalsList.length > 1 && (
                            <div className="mb-2">
                              <strong>{index + 1}.</strong>
                            </div>
                          )}
                          <div className="table-responsive">
                            <table className="table table-bordered border table-sm">
                              <thead>
                                <tr>
                                  <th>Height</th>
                                  <th>Weight</th>
                                  <th>Pulse</th>
                                  <th>BP</th>
                                  <th>SpO2</th>
                                  <th>Temperature</th>
                                  <th>Respiratory Rate</th>
                                  <th>Blood Glucose</th>
                                  <th>BSA</th>
                                  <th>BMI</th>
                                  <th>Record Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{vital.height || "N/A"} cm</td>
                                  <td>{vital.weight || "N/A"} kg</td>
                                  <td>{vital.pulse || "N/A"} bpm</td>
                                  <td>{vital.bloodPressure || "N/A"} mmHg</td>
                                  <td>{vital.spo2 || "N/A"}%</td>
                                  <td>{vital.temperature || "N/A"}°F</td>
                                  <td>{vital.respiratoryRate || "N/A"} BRPM</td>
                                  <td>{vital.bloodGlucose || "N/A"} mg/dL {vital.glucoseTestType || ""}</td>
                                  <td>{vital.glucoseTestType || "N/A"}</td>
                                  <td>{vital.bsa || "N/A"} m²</td>
                                  <td>{vital.bmi || "N/A"} kg/m²</td>
                                  <td>{vital.recordTime || "N/A"}</td>

                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))
                    ) : (
                      /* Fallback if no vitals data is found */
                      <div className="table-responsive">
                        <table className="table table-bordered border table-sm">
                          <thead>
                            <tr>
                              <th>Height</th>
                              <th>Weight</th>
                              <th>Pulse</th>
                              <th>BP</th>
                              <th>SpO2</th>
                              <th>Temperature</th>
                              <th>Respiratory Rate</th>
                              <th>Blood Glucose</th>
                              <th>BSA</th>
                              <th>BMI</th>
                              <th>Record Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan="11" className="text-center">No vitals data available</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Chief Complaints/Symptoms Section */}
                {((visit.complaints && visit.complaints.length > 0) || (visit.complaintsList && visit.complaintsList.length > 0)) && (
                  <div className="mb-4">
                    <strong>Chief Complaints/Symptoms</strong>
                    <div className="table-responsive">
                      <table className="table table-bordered border table-sm">
                        <thead>
                          <tr>
                            <th>Complaint</th>
                            <th>Frequency</th>
                            <th>Severity</th>
                            <th>Duration</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* For formatted data */}
                          {visit.complaints && visit.complaints.filter(c => c.complaint).map((complaint, index) => (
                            <tr key={`display-complaint-${index}`}>
                              <td>{complaint.complaint || "N/A"}</td>
                              <td>{complaint.frequency || "N/A"}</td>
                              <td>{complaint.severity || "N/A"}</td>
                              <td>{complaint.duration || "N/A"}</td>
                              <td>{complaint.date ? formatDate(complaint.date) : "N/A"}</td>
                            </tr>
                          ))}
                          {/* For API response data */}
                          {visit.complaintsList && visit.complaintsList.map((complaint, index) => (
                            <tr key={`display-raw-complaint-${index}`}>
                              <td>{complaint.complaint || "N/A"}</td>
                              <td>{complaint.frequency || "N/A"}</td>
                              <td>{complaint.severity || "N/A"}</td>
                              <td>{complaint.duration || "N/A"}</td>
                              <td>{complaint.complaintDate ? formatDate(complaint.complaintDate) : "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* History of Present Illness Section */}
                {(visit.historyOfPresentIllness || (visit.presentIllnessList && visit.presentIllnessList.length > 0)) && (
                  <div className="mb-4">
                    <strong>History of Present Illness</strong>
                    <div style={{ whiteSpace: "pre-line" }}>
                      {visit.historyOfPresentIllness ||
                        (visit.presentIllnessList && visit.presentIllnessList[0]?.historyText) ||
                        "N/A"}
                    </div>
                  </div>
                )}

                {/* Clinical Assessment Section */}
                {(visit.clinicalAssessment ||
                  (visit.clinicalAssessmentList && visit.clinicalAssessmentList.length > 0)) && (
                    <div className="mb-4">
                      <strong>Clinical Assessment</strong>

                      {/* Status, ECOG, KPS */}
                      <div className="row mt-2">
                        <div className="col-md-4">
                          <p><strong>Status:</strong> {
                            visit.clinicalAssessment?.statusOfDisease ||
                            (visit.clinicalAssessmentList && visit.clinicalAssessmentList[0]?.diseaseStatus) ||
                            "N/A"
                          }</p>
                        </div>
                        <div className="col-md-4">
                          <p><strong>ECOG:</strong> {
                            visit.clinicalAssessment?.ecog ||
                            (visit.clinicalAssessmentList && visit.clinicalAssessmentList[0]?.ecog) ||
                            "N/A"
                          }</p>
                        </div>
                        <div className="col-md-4">
                          <p><strong>KPS:</strong> {
                            visit.clinicalAssessment?.kps ||
                            (visit.clinicalAssessmentList && visit.clinicalAssessmentList[0]?.kps) ||
                            "N/A"
                          }</p>
                        </div>
                      </div>
                    </div>
                  )}
                {/* On Examination */}
                {(visit.clinicalAssessment?.onExamination ||
                  (visit.clinicalAssessmentList && visit.clinicalAssessmentList[0]?.examinationNotes)) && (
                    <div>
                      <p><strong>On Examination:</strong> {
                        visit.clinicalAssessment?.onExamination ||
                        (visit.clinicalAssessmentList && visit.clinicalAssessmentList[0]?.examinationNotes) ||
                        "N/A"
                      }</p>
                    </div>
                  )}



                {/* Secondary Diagnosis Section */}
                {((visit.secondaryDiagnosis && visit.secondaryDiagnosis.some(d => d.diagnosis)) ||
                  (visit.secondaryDiagnosisList && visit.secondaryDiagnosisList.length > 0)) && (
                    <div className="mb-4">
                      <strong>Secondary Diagnosis</strong>
                      <div className="table-responsive">
                        <table className="table table-bordered border table-sm">
                          <thead>
                            <tr>
                              <th>Diagnosis</th>
                              <th>Duration</th>
                              <th>Comments</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* For formatted data */}
                            {visit.secondaryDiagnosis && visit.secondaryDiagnosis.filter(d => d.diagnosis).map((diagnosis, index) => (
                              <tr key={`display-diagnosis-${index}`}>
                                <td>{diagnosis.diagnosis || "N/A"}</td>
                                <td>{diagnosis.duration || "N/A"}</td>
                                <td>{diagnosis.comments || "N/A"}</td>
                                <td>{diagnosis.date ? formatDate(diagnosis.date) : "N/A"}</td>
                              </tr>
                            ))}
                            {/* For API response data */}
                            {visit.secondaryDiagnosisList && visit.secondaryDiagnosisList.map((diagnosis, index) => (
                              <tr key={`display-raw-diagnosis-${index}`}>
                                <td>{diagnosis.diagnosis || "N/A"}</td>
                                <td>{diagnosis.duration || "N/A"}</td>
                                <td>{diagnosis.comments || "N/A"}</td>
                                <td>{diagnosis.diagnosisDate ? formatDate(diagnosis.diagnosisDate) : "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

               {/* Prescription Section*/}
{(() => {
  // Get medicines from medicine list for this visit
  const xrefMedicines = getMedicinesForVisit(visit.visitId) || [];
  
  return (
    (visit.prescription && visit.prescription.length > 0) ||
    (visit.prescriptionList && visit.prescriptionList.length > 0) ||
    (xrefMedicines.length > 0)
  ) && (
    <div className="mb-4">
      <strong>Doctor Prescription</strong>
      <div className="table-responsive">
        <table className="table table-bordered border table-sm">
          <thead>
            <tr>
              <th>Medicine</th>
              <th>Dose</th>
              <th>When</th>
              <th>Duration</th>
              <th>Instructions</th>
            </tr>
          </thead>
          <tbody>
            {/* Formatted Data */}
            {visit.prescription &&
              visit.prescription.filter(med => med.medicine).map((med, index) => (
                <tr key={`display-med-${index}`}>
                  <td>{med.medicine || "N/A"}</td>
                  <td>{med.dose || "N/A"}</td>
                  <td>{med.when || "N/A"}</td>
                  <td>{med.duration || "N/A"}</td>
                  <td>{med.instructions || "N/A"}</td>
                </tr>
              ))}

            {/* Medicine List Data - NEW - COMMENTED OUT TO AVOID DUPLICATES */}
            {/* Since visit.prescription already contains all medicines, we don't need to display xrefMedicines separately */}
            {/* {xrefMedicines.filter(med => med.medicine).map((med, index) => (
              <tr key={`display-xref-med-${index}`} 
                 >
                <td>{med.medicine || "N/A"}</td>
                <td>{med.dose || "N/A"}</td>
                <td>{med.when || "N/A"}</td>
                <td>{med.duration || "N/A"}</td>
                <td>{med.instructions || "N/A"}</td>
              </tr>
            ))} */}

            {/* API raw data */}
            {visit.prescriptionList &&
              visit.prescriptionList
                .filter(med => med.visitId === visit.visitId && med.medicineName)
                .map((med, index) => (
                  <tr key={`display-raw-med-${index}`}>
                    <td>{med.medicineName || "N/A"}</td>
                    <td>{med.dosage || "N/A"}</td>
                    <td>{med.frequency || "N/A"}</td>
                    <td>{med.duration || "N/A"}</td>
                    <td>{med.instructions || "N/A"}</td>
                  </tr>
                ))}

            {/* Fallback message */}
            {(!visit.prescription || visit.prescription.length === 0) &&
              (!visit.prescriptionList || visit.prescriptionList.length === 0) &&
              (xrefMedicines.length === 0) && (
                <tr>
                  <td colSpan="5" className="text-center">No prescriptions available</td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
})()}


                {/* Treatment Section */}
                {((visit.treatment && visit.treatment.some(t => t.treatmentType)) ||
                  (visit.treatmentList && visit.treatmentList.length > 0)) && (
                    <div className="mb-4">
                      <strong>Treatment</strong>
                      <div className="table-responsive">
                        <table className="table table-bordered border table-sm">
                          <thead>
                            <tr>
                              <th>Treatment Type</th>
                              <th>Description</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* For formatted data */}
                            {visit.treatment && visit.treatment.map((treatment, index) => (
                              <tr key={`display-treatment-${index}`}>
                                <td>{treatment.treatmentType || "N/A"}</td>
                                <td>{treatment.description || "N/A"}</td>
                                <td>{treatment.date ? formatDate(treatment.date) : "N/A"}</td>
                              </tr>
                            ))}
                            {/* For API response data */}
                            {visit.treatmentList && visit.treatmentList.map((treatment, index) => (
                              <tr key={`display-raw-treatment-${index}`}>
                                <td>{treatment.treatmentType || "N/A"}</td>
                                <td>{treatment.description || "N/A"}</td>
                                <td>{treatment.treatmentDate ? formatDate(treatment.treatmentDate) : "N/A"}</td>
                              </tr>
                            ))}
                            {/* If both are empty, show a message */}
                            {(!visit.treatment || visit.treatment.length === 0) &&
                              (!visit.treatmentList || visit.treatmentList.length === 0) && (
                                <tr>
                                  <td colSpan="3" className="text-center">No treatments available</td>
                                </tr>
                              )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {/* Tests Requested Section */}
                {(visit.testsRequested || (visit.testsRequestedList && visit.testsRequestedList.length > 0)) && (
                  <div className="mb-4">
                    <strong>Test(s) Prescribed</strong>
                    <div>{visit.testsRequested ||
                      (visit.testsRequestedList && visit.testsRequestedList[0]?.testName) ||
                      "N/A"}</div>
                  </div>
                )}

                {/* Advice/Notes Section */}
                {(visit.adviceNotes || (visit.adviceList && visit.adviceList.length > 0)) && (
                  <div className="mb-4">
                    <strong>Advice/Notes</strong>
                    <div>{visit.adviceNotes ||
                      (visit.adviceList && visit.adviceList[0]?.adviceNotes) ||
                      "N/A"}</div>
                  </div>
                )}

                {/* Review Notes Section */}
                {(visit.reviewNotes || (visit.adviceList && visit.adviceList.length > 0 && visit.adviceList[0]?.reviewNotes)) && (
                  <div className="mb-4">
                    <strong>Review Notes</strong>
                    <div>{visit.reviewNotes ||
                      (visit.adviceList && visit.adviceList[0]?.reviewNotes) ||
                      "N/A"}</div>
                  </div>
                )}

                {/* Refer To Section */}
                {(visit.referTo || (visit.adviceList && visit.adviceList.length > 0 && visit.adviceList[0]?.referTo)) && (
                  <div className="mb-4">
                    <strong>Refer To</strong>
                    <div>{visit.referTo ||
                      (visit.adviceList && visit.adviceList[0]?.referTo) ||
                      "N/A"}</div>
                  </div>
                )}

                {/* Next Visit Date Section */}
                {(visit.nextVisitDate || (visit.nextVisitList && visit.nextVisitList.length > 0)) && (
                  <div className="mb-4">
                    <strong>Next Visit Date</strong>
                    <div>
                      <p><strong>Date:</strong> {formatDate(visit.nextVisitDate) ||
                        (visit.nextVisitList && visit.nextVisitList[0]?.nextVisitDate && formatDate(visit.nextVisitList[0].nextVisitDate)) ||
                        "N/A"}</p>
                      <p><strong>Duration:</strong> {visit.nextVisitDuration ||
                        (visit.nextVisitList && visit.nextVisitList[0]?.duration) ||
                        "N/A"}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="alert alert-info">No OP visits found for this patient.</div>
        )}
      </div>
    </div>
  );
});

export default OPVisitManagement;



